import type { TranslationKeys } from "../types.js";
import { marketing as marketingPt } from "./marketing/pt.js";

export const pt: TranslationKeys = {
  common: {
    loading: "Carregando...",
    error: "Erro",
    success: "Sucesso",
    cancel: "Cancelar",
    confirm: "Confirmar",
    save: "Salvar",
    delete: "Excluir",
    edit: "Editar",
    create: "Criar",
    update: "Atualizar",
    search: "Procurar",
    filter: "Filtro",
    actions: "Ações",
    back: "Voltar",
    next: "Próximo",
    previous: "Anterior",
    close: "Fechar",
    description: "Otimizado para SEO com renderização no lado do servidor (SSR) e componentes de servidor",
    locale: {
      label: "Idioma da interface",
      hint: "Cópia do painel e Accept-Language enviados para a API.",
      es: "Espanhol",
      en: "Inglês",
      fr: "Francês",
      pt: "Português",
    },
    theme: {
      label: "Aparência",
      light: "Claro",
      dark: "Escuro",
      system: "Sistema",
    },
  },
  auth: {
    login: "Entrar",
    logout: "sair",
    register: "Inscrever-se",
    email: "E-mail",
    password: "Senha",
    forgotPassword: "Esqueceu sua senha?",
    resetPassword: "Redefinir senha",
    rememberMe: "Lembre de mim",
    welcome: "Bem-vindo",
    welcomeBack: "bem vindo de volta",
  },
  email: {
    welcome: {
      subject: "Bem-vindo à nossa plataforma!",
      title: "Olá {{name}}!",
      content: "Obrigado por se juntar à nossa plataforma. ",
      buttonText: "Comece",
    },
    verification: {
      subject: "Verifique seu e-mail",
      title: "Verifique seu e-mail",
      content: "Clique no link a seguir para verificar seu endereço de e-mail:",
      buttonText: "Verificar e-mail",
      expiresIn: "Este link irá expirar em 24 horas.",
    },
    passwordReset: {
      subject: "Redefinir sua senha",
      title: "Recuperação de senha",
      content: "Clique no link a seguir para redefinir sua senha:",
      buttonText: "Redefinir senha",
      expiresIn: "Este link irá expirar em 1 hora.",
      ignore: "Se você não solicitou essa alteração, ignore este e-mail.",
    },
    houseInvitation: {
      subject: "Você está convidado a ingressar em uma família no HomeExpense",
      title: "Convite doméstico",
      greeting: "{{inviterName}} convidou você para participar de “{{houseName}}” no HomeExpense.",
      content:
        "Crie sua conta ou faça login com este e-mail para aceitar o convite e começar a compartilhar despesas.",
      buttonRegister: "Crie uma conta e participe",
      loginHint: "Já tem uma conta?",
      loginCta: "Faça login com este link",
      expiresIn: "Este convite expira em 14 dias.",
      footer: "Se você não esperava este e-mail, pode ignorá-lo.",
    },
  },
  errors: {
    generic: "Ocorreu um erro",
    notFound: "Não encontrado",
    unauthorized: "Não autorizado",
    forbidden: "Proibido",
    validation: "Erro de validação",
    serverError: "Erro no servidor",
    networkError: "Erro de rede",
  },
  validation: {
    required: "Este campo é obrigatório",
    email: "E-mail inválido",
    minLength: "Deve ter pelo menos {{min}} caracteres",
    maxLength: "Não pode exceder {{max}} caracteres",
    passwordMismatch: "As senhas não coincidem",
  },
  dashboardUi: {
    shell: {
      brandTitle: "HomeExpense",
      brandTagline: "Gerenciamento claro de despesas domésticas",
      brandSlogan: "Construindo o futuro da sua família,\numa despesa de cada vez.",
      addExpense: "Adicionar despesa",
      searchPlaceholder: "Pesquise HomeExpense…",
      userStubName: "Usuário",
      userStubRole: "Membro",
      notificationsComingSoon: "Notificações (em breve)",
      noActiveHouse: "Nenhuma casa selecionada",
      openingBalanceLine: "Saldo inicial: {{amount}}",
    },
    nav: {
      mainNavigation: "Navegação principal",
      dashboard: "Painel",
      journal: "Jornal",
      search: "Procurar",
      budget: "Orçamento",
      analytics: "Percepções",
      vault: "Cofre",
      scan: "Varredura de IA",
      financings: "Financiamento",
      houses: "Casas",
      profile: "Perfil",
      accountData: "Conta e dados",
      settings: "Configurações",
      support: "Apoiar",
    },
    placeholder: {
      hint: "Tela em construção (fase UI). ",
    },
    pages: {
      home: "Página inicial do painel",
      login: "Entrar",
      register: "Inscrever-se",
      forgotPassword: "Esqueceu sua senha",
      resetPassword: "Redefinir senha",
      settingsPassword: "Alterar a senha",
      settingsPasskeys: "Passkeys",
      settingsProfile: "Perfil de usuário",
      settingsPersonal: "Dados pessoais",
      settings: "Configurações",
      support: "Apoiar",
      houses: "Minhas casas",
      houseMembers: "Membros da casa",
      expenses: "Diário de despesas",
      financings: "Financiamento",
      financingNew: "Novo financiamento",
      financingDetail: "Detalhes de financiamento",
      financingEdit: "Editar financiamento",
      expenseNew: "Nova despesa",
      expenseDetail: "Detalhe da despesa",
      expensePaidFeedback: "Despesa paga",
      budget: "Orçamento mensal",
      vault: "Cofre de garantia",
      vaultNew: "Registrar ativo",
      vaultDetail: "Detalhe do recurso",
      scan: "Varredura de IA",
      houseAnalytics: "Informações sobre a família",
      search: "Procurar",
      notifications: "Notificações",
      notificationSettings: "Configurações de notificação",
      settingsDevices: "Dispositivos e sessões",
      onboarding: "Configuração inicial",
    },
    searchPage: {
      subtitle: "Encontre despesas e itens do cofre em sua casa ativa.",
      backHome: "De volta para casa",
      noActiveHouse: "Escolha uma casa ativa para pesquisar seu diário e cofre.",
      minCharsHint: "Digite pelo menos 2 caracteres para pesquisar.",
      emptyPrompt:
        "Use o campo acima para pesquisar títulos, notas ou categorias de despesas, ou nomes e notas de cofres.",
      resultsFor: "Resultados para “{{query}}”",
      openJournalWithQuery: "Abra o diário completo",
      noResults: "Nenhuma correspondência. ",
      sectionExpenses: "Despesas",
      sectionVault: "Cofre",
    },
    states: {
      loadingHint: "Carregando dados…",
      emptyTitle: "Nada aqui ainda",
      emptyHint: "Quando houver dados, eles aparecerão aqui.",
      errorTitle: "Algo deu errado",
      errorHint: "Tente novamente ou verifique sua conexão.",
      retry: "Tentar novamente",
    },
    mobile: {
      openMenu: "Abrir menu",
      homeLink: "Lar",
    },
    analytics: {
      openFromHome: "Ver insights de gastos (últimos meses)",
      openFromBudget: "Abra insights e gráficos da família",
      backToBudget: "Voltar ao orçamento",
      missingHouse: "Selecione uma casa para ver análises.",
      subtitle: "Gastos pagos versus orçamento planejado por mês, tendências de categoria e dicas de orçamento (últimos {{count}} meses).",
      empty: "Ainda não há despesas pagas neste período. ",
      chartTitle: "Pago vs planejado por mês",
      chartPaid: "Pago",
      chartPlanned: "Planejado",
      chartAvgLine: "Média paga (meses com gastos)",
      peakMonthTitle: "Mês de maior gasto",
      peakMonthBody: "{{month}} — {{amount}} pago",
      peakMonthNone: "Nenhum gasto registrado nesta janela.",
      avgSpendTitle: "Média paga por mês ativo",
      avgSpendHint: "Somente meses com gastos pagos > 0 são incluídos.",
      topCategoriesTitle:
        "Principais categorias por gasto acumulado (últimos {{count}} meses)",
      otherCategories: "Todas as outras categorias combinadas: {{amount}}",
      overspendLeaderTitle:
        "Maior ultrapassagem acumulada do plano (últimos {{count}} meses)",
      overspendLeaderBody:
        "“{{name}}” acumula {{amount}} acima do plano nos meses com linha de orçamento, somando apenas os últimos {{count}} meses.",
      reviewTitle: "Categorias para revisar",
      reviewHint: "Muitas vezes acima do plano em meses com uma linha orçamentária – considere aumentar o plano ou cortar gastos.",
      reviewEmpty: "Nenhuma categoria corresponde a este padrão na janela selecionada.",
      reduceTitle: "Espaço para diminuir o plano",
      reduceHint: "Muitas vezes abaixo do planejado – você pode reduzir com segurança a alocação mensal.",
      reduceEmpty: "Nenhuma categoria corresponde a este padrão na janela selecionada.",
    },
    onboarding: {
      pageTitle: "Configure seu espaço em três etapas",
      progress: "Etapa {{current}} de {{total}}",
      stepWelcomeTitle: "Bem-vindo ao HomeExpense",
      stepWelcomeBody:
        "Iremos orientá-lo na criação ou confirmação da sua casa, convidar pessoas que dividam despesas e definir um limite mensal como referência. ",
      stepHouseTitle: "Nome da casa",
      stepHouseBody: "É aqui que você agrupará despesas, orçamento e membros.",
      stepHouseHasHouseTitle: "Sua casa",
      stepHouseHasHouseBody:
        'You are already part of “{{name}}”. You can continue or create another home from “Homes” whenever you like.',
      stepMembersTitle: "Quem divide a casa",
      stepMembersBody:
        "Convide parceiros ou colegas de apartamento por e-mail. ",
      inviteEmailLabel: "E-mail para convidar",
      inviteEmailPlaceholder: "nome@exemplo.com",
      inviteSend: "Enviar convite",
      inviteSending: "Enviando…",
      inviteSent: "Convite enviado.",
      inviteSkipHint: "Você pode convidar mais tarde membros da Câmara.",
      stepBudgetTitle: "Limite mensal (referência)",
      stepBudgetBody:
        "Um limite mensal flexível ajuda o painel a mostrar o ritmo de gastos. ",
      budgetAmountLabel: "Valor mensal ({{currency}})",
      budgetSkipHint: "Você pode deixar em branco e configurá-lo mais tarde.",
      stepDoneTitle: "Tudo pronto",
      stepDoneBody: "Você pode usar o painel agora. ",
      startTour: "Iniciar tour pelo painel",
      goHome: "Vá para casa",
      skipAll: "Pule e abra o painel",
      next: "Próximo",
      back: "Voltar",
      creatingHouse: "Criando…",
      savingBudget: "Salvando…",
      finishing: "Acabamento…",
      houseNameLabel: "Nome da casa",
      houseNamePlaceholder: "por exemplo ",
      invalidAmount: "Insira um valor válido (um número maior que zero).",
    },
    productTour: {
      sidebarHomeTitle: "Painel",
      sidebarHomeDesc: "Volte aqui para obter a visão geral da página inicial ativa: saldo, gastos mensais e links rápidos.",
      navHousesTitle: "Casas",
      navHousesDesc:
        "Crie uma casa, escolha a ativa e abra configurações de membros ou por casa. ",
      navJournalTitle: "Jornal",
      navJournalDesc:
        "Lista de despesas da casa ativa: pontuais e recorrentes, filtros e detalhe. ",
      navBudgetTitle: "Orçamento",
      navBudgetDesc:
        "Limite mensal, plano de categoria e acompanhamento de gastos reais. ",
      homeMembersTitle: "Membros da família",
      homeMembersDesc:
        "Convites abertos, funções e divisão padrão. ",
      sidebarAddExpenseTitle: "Nova despesa",
      sidebarAddExpenseDesc: "Registre uma despesa única ou abra o formulário para adicioná-la ao diário.",
      sidebarAddExpenseClosingDesc:
        "Use isso a qualquer momento para registrar gastos. A visita se encerra: agora você sabe criar uma casa, convidar pessoas, escolher se uma despesa é compartilhada (lar) ou pessoal, indicar quem pagou e planejar o orçamento.",
      housesCreateTourTitle: "Crie sua casa",
      housesCreateTourDesc:
        "Insira um nome e crie: ele contém despesas, membros e orçamento. ",
      housesEmptyTourTitle: "Ainda não há casas",
      housesEmptyTourDesc:
        "Depois de criar o primeiro você verá o resumo aqui e poderá abrir Membros e Orçamento. ",
      housesMembersLinkTourTitle: "Membros da família",
      housesMembersLinkTourDesc:
        "Dos Membros você convida por e-mail, define funções e a divisão padrão para despesas compartilhadas. ",
      membersInviteTourTitle: "Convide para casa",
      membersInviteTourDesc:
        "Insira o e-mail e a função (membro ou administrador). ",
      membersSplitTourTitle: "Divisão entre membros",
      membersSplitTourDesc:
        "Os percentuais totalizam 100% e definem a participação de cada pessoa nas despesas compartilhadas. ",
      budgetHeroTourTitle: "Orçamento por mês",
      budgetHeroTourDesc:
        "Escolha o mês com as setas ou o seletor de mês. ",
      budgetGoalTourTitle: "Meta e barra de progresso",
      budgetGoalTourDesc:
        "A barra compara o que já foi pago neste mês com o teto ou valor previsto por categoria. ",
      homeQuickActionsTitle: "Ações rápidas",
      homeQuickActionsDesc: "Atalhos para adicionar uma despesa, digitalizar um recibo ou gerenciar membros.",
      settingsReplay: "Mostrar o tour do painel novamente",
      settingsReplayHint: "Abre a página inicial com o passo a passo da interface guiada.",
      fallbackTitle: "Seu painel",
      fallbackBody:
        "Use o menu lateral (ou celular): Casas para cadastrar o domicílio, Diário e Orçamento para trabalhar, e de casa os links rápidos para associados e despesas.",
    },
    auth: {
      appName: "HomeExpense",
      loginTitle: "bem vindo de volta",
      loginSubtitle: "Insira suas credenciais para acessar seu diário financeiro.",
      registerTitle: "Crie seu espaço",
      registerSubtitle: "Cadastre-se para gerenciar as despesas domésticas com tranquilidade editorial.",
      forgotTitle: "Recuperar acesso",
      forgotSubtitle: "Digite seu e-mail e enviaremos um link para redefinir sua senha.",
      resetTitle: "Nova Senha",
      resetSubtitle: "Escolha uma senha forte para sua conta.",
      name: "Nome",
      namePlaceholder: "Seu nome",
      emailLabel: "E-mail",
      emailPlaceholder: "nome@exemplo.com",
      passwordLabel: "Senha",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirme sua senha",
      signInCta: "Entrar",
      signUpCta: "Criar uma conta",
      sendResetLink: "Enviar link",
      saveNewPassword: "Salvar senha",
      createAccountLink: "Crie seu espaço",
      registerPrompt: "Não tem uma conta?",
      alreadyHaveAccount: "Já tem uma conta?",
      backToLogin: "Voltar para fazer login",
      forgotLink: "Esqueceu sua senha?",
      forgotSentHint: "Se existir uma conta para esse e-mail, você receberá um link em breve.",
      resetSuccess: "Senha atualizada. ",
      invalidResetToken: "Este link é inválido ou expirou. ",
      signingIn: "Fazendo login…",
      signingUp: "Criando conta…",
      inviteLoginBanner: "Faça login com o e-mail de convite para ingressar na família.",
      dividerOr: "ou",
      continueWithGoogle: "Continuar com o Google",
      continueWithApple: "Continuar com a Apple",
      continueWithPasskey: "Continuar com Passkey",
      signUpWithPasskey: "Cadastrar com Passkey",
      registerWithPasskeyAfterSignUp: "Registrar passkey após criar a conta",
      continuingWithGoogle: "Conectando-se ao Google…",
      continuingWithApple: "Conectando-se com a Apple…",
      continuingWithPasskey: "Conectando-se com Passkey…",
      passkeyPreferredHint:
        "Se já registou uma passkey, costuma aparecer primeiro; o navegador pode sugeri-la ao tocar em continuar.",
      errors: {
        generic: "Algo deu errado. ",
        invalidEmailOrPassword: "E-mail ou senha inválidos.",
        userAlreadyExists: "Já existe uma conta com este e-mail.",
        passwordTooShort: "A senha é muito curta.",
        passwordTooLong: "A senha é muito longa.",
        invalidEmail: "O e-mail não é válido.",
        invalidToken: "Este link é inválido ou expirou.",
        resetDisabled: "A recuperação de senha não está disponível.",
        invalidOrigin: "Origem não permitida. ",
        network: "Erro de rede. ",
        emailNotVerified: "Verifique seu e-mail antes de fazer login.",
        invalidPassword: "A senha atual está incorreta.",
        credentialAccountNotFound: "Esta conta não tem senha (por exemplo, apenas login social). ",
        providerNotConfigured: "O login do Google não está disponível. ",
      },
    },
    houses: {
      pageTitle: "Minhas casas",
      createTitle: "Casa nova",
      nameLabel: "Nome",
      namePlaceholder: "por exemplo ",
      createCta: "Criar casa",
      creating: "Criando…",
      yourRole: "Seu papel",
      membersLink: "Membros",
      setActive: "Use esta casa",
      emptyTitle: "Você ainda não tem casas",
      emptyHint: "Crie uma casa para começar a monitorar as despesas da sua família.",
      cardSummaryPeriod: "Resumo {{yearMonth}}",
      cardPlannedShort: "Planejado",
      cardSpentShort: "Pago",
      cardUsageShort: "Uso",
      cardStatusWithin: "Dentro do orçamento",
      cardStatusTight: "Perto do limite",
      cardStatusOver: "Acima do orçamento",
      cardStatusNeutral: "Sem limite ou plano mensal",
      cardSummaryError: "Não foi possível carregar o resumo",
      openingBalanceLabel: "Abertura de saldo bancário",
      openingBalanceEdit: "Editar saldo inicial",
      openingBalanceSave: "Salvar",
      openingBalanceCancel: "Cancelar",
      openingBalanceSaving: "Salvando…",
      openingBalanceAmountLabel: "Quantia",
      openingBalanceHint: "Saldo bancário de referência na montagem da casa (unidades menores: cêntimos). ",
      openingBalanceReadOnlyHint: "Somente proprietários ou administradores podem alterar o saldo inicial.",
      openingBalanceInvalidAmount: "Insira um valor válido (0 ou superior).",
      openingBalanceLoadError: "Não foi possível carregar o saldo inicial.",
      financialProfileSectionTitle: "Perfil financeiro",
      financialProfileSubtitle: "Saldo inicial de referência, moeda da casa e limite mensal opcional.",
      financialProfileEdit: "Editar perfil financeiro",
      financialProfileFinancingsLink: "Ver financiamentos",
      currencyLabel: "Moeda (ISO 4217)",
      currencyPlaceholder: "Código de três letras, ex. EUR",
      monthlyCapLabel: "Limite mensal (opcional)",
      monthlyCapPlaceholder: "Ex. 2500,50",
      monthlyCapHint: "Limite de referência da casa; deixe vazio para remover o limite.",
      monthlyCapNone: "Sem limite mensal",
      financialProfileReadOnlyHint: "Apenas proprietários ou administradores podem editar o perfil financeiro.",
      financialProfileInvalidCurrency: "A moeda deve ser um código ISO com exatamente 3 letras (ex. EUR).",
      monthlyCapInvalidAmount: "Introduza um valor válido para o limite ou deixe o campo vazio.",
      financialProfileSaveSuccess: "Perfil financeiro guardado.",
      financialProfileLoadError: "Não foi possível carregar o perfil financeiro.",
      setActiveSuccess: "Residência ativa atualizada.",
      editNameCta: "Editar nome",
      editNameSave: "Guardar nome",
      editNameCancel: "Cancelar",
      deleteCta: "Eliminar residência",
      deleteConfirm:
        "Eliminar esta residência? Os dados associados serão removidos. Esta ação não pode ser anulada.",
      updateNameSuccess: "Nome da residência atualizado.",
      updateNameError: "Não foi possível atualizar o nome. Tente novamente.",
      deleteSuccess: "Residência eliminada.",
      deleteError: "Não foi possível eliminar a residência. Tente novamente.",
    },
    members: {
      pageTitle: "Membros",
      roleLabel: "Papel",
      roleAdmin: "Administrador",
      roleMember: "Membro",
      removeCta: "Remover",
      removeConfirm: "Remover este membro da casa?",
      columnUser: "Usuário",
      columnRole: "Papel",
      columnSplit: "Dividir (%)",
      backToHouses: "De volta às casas",
      inviteSectionTitle: "Convidar por e-mail",
      inviteSectionAdminOnly: "Apenas proprietários e administradores podem convidar ou gerir membros.",
      inviteHelp:
        "Enviaremos por e-mail um link para criar uma conta ou fazer login. O convite deve ser aceito com o mesmo endereço de e-mail.",
      inviteEmailLabel: "E-mail do convidado",
      inviteEmailPlaceholder: "nome@exemplo.com",
      inviteSendCta: "Enviar convite",
      inviteSending: "Enviando…",
      inviteSent: "Convite enviado. ",
      pendingInvitesTitle: "Convites pendentes",
      pendingInviteExpires: "Expira em {{date}}",
      pendingInviteExpiredBadge: "Expirado",
      revokeInviteCta: "Revogar",
      revokeInviteConfirm: "Revogar este convite?",
      registerInviteHeadline: "Você está convidado",
      registerInviteSubline: "Cadastre-se em “{{houseName}}” — use o e-mail para o qual este convite foi enviado.",
      emailLockedForInvite: "Este e-mail é definido pelo seu convite.",
      inviteAcceptWrongEmail: "Faça login com o e-mail para o qual o convite foi enviado.",
      inviteAcceptedJoined: "Você se juntou à família.",
      inviteAcceptedAlreadyMember: "Você já era membro; ",
      removeOwnerNotAllowed: "O proprietário não pode ser removido daqui.",
      removeMemberSuccess: "Membro removido da residência.",
      changeRoleSaved: "Função atualizada.",
      roleOwner: "Proprietário",
      splitSectionTitle: "Configuração dividida",
      splitSectionHelp:
        "Defina a participação de cada membro no orçamento mensal. ",
      splitBudgetEyebrow: "Orçamento mensal",
      splitNoBudgetHint:
        "Sem um limite mensal não podemos mostrar valores por pessoa. ",
      splitOpenBudgetLink: "Ir para o orçamento",
      splitYouBadge: "Você",
      splitTotalLine: "Total: {{percent}}%",
      splitTotalValid: "As divisões somam 100%",
      splitRemainderHint: "Calculado automaticamente para atingir 100%.",
      splitSaveCta: "Salvar divisão",
      splitSaving: "Salvando…",
      splitSaveSuccess: "Divisão salva.",
      splitReadOnlyHint: "Somente administradores e proprietários podem alterar a divisão.",
    },
    home: {
      periodLabel: "Período {{yearMonth}}",
      spentMonth: "Passei este mês",
      totalToContributeLabel: "Total a contribuir este mês",
      plannedBudget: "Orçamento planejado",
      budgetUsage: "Uso do orçamento",
      openingBalance: "Saldo de referência",
      upcomingTitle: "Próximas despesas",
      upcomingEmpty: "Não há datas de vencimento futuras.",
      ledgerRecentEmpty:
        "Ainda não há despesas para esta casa. ",
      quickActions: "Ações rápidas",
      actionExpense: "Adicionar despesa",
      actionScan: "Digitalizar recibo",
      actionMembers: "Gerenciar membros",
      insightsStub: "Comparações e dicas de economia aparecerão aqui em breve.",
      heroOpeningLabel: "Abertura de saldo bancário",
      heroProjectedBalanceLabel: "Saldo estimado da conta",
      heroProjectedBalanceHint:
        "Base {{opening}} + orçamento {{need}} − pago {{spent}} − retido {{retained}}",
      heroStatSpentLabel: "Pago no período",
      heroStatForecastExpensesLabel: "Previsão de despesas no período",
      heroStatPlannedLabel: "Orçamento planejado",
      insightBadge: "Visão inteligente",
      insightTitleWithin: "Você está no caminho certo",
      insightTitleTight: "Observe o orçamento",
      insightTitleOver: "Orçamento excedido",
      insightTitleNeutral: "Visão geral do período",
      insightBodyWithin:
        "Você usou {{percent}}% do orçamento planejado este mês – espaço para fazer ajustes com calma.",
      insightBodyTight: "Grande parte do orçamento já foi utilizada. ",
      insightBodyOver: "Os gastos excedem o orçamento planejado ({{percent}}%). ",
      insightBodyNeutral: "Defina um limite mensal no perfil financeiro da casa para rastreamento automático.",
      insightCta: "Veja detalhes no diário",
      ledgerEyebrow: "O livro-razão",
      ledgerTitle: "Movimentos recentes",
      ledgerViewAll: "Abra o diário completo",
      budgetProgressSubline: "{{spent}} de {{cap}} usado",
      gridSpentHint: "Este mês",
      gridForecastHint: "Previsão",
      forecastExpensesMonth: "Comprometidos (todas as cobranças)",
      gridBudgetHint: "Plano",
      gridUsageHint: "Uso",
      gridSpentSharedLine: "Casa (partilhado)",
      gridSpentPersonalLine: "Pessoal (pagador único)",
      gridBudgetMonthlyLabel: "Orçamento mensal",
      ledgerRowBadge: "Por vir",
      moneySnapshotTitle: "Resumo do dinheiro do mês",
      moneySnapshotOpening: "Saldo inicial",
      moneySnapshotNeedMonth: "Necessário neste mês",
      moneySnapshotRetained: "Retido (rateados)",
      moneySnapshotAvailableAfterRetained: "Disponível após retenções",
      moneySnapshotProjectedBalance: "Saldo estimado da conta",
      budgetSetupUrgentTitle: "Ainda sem referência de orçamento do mês",
      budgetSetupUrgentBody:
        "Sem linhas por categoria nem teto mensal da casa, o acompanhamento perde força. Configure em Orçamento.",
      budgetSetupUrgentCta: "Abrir orçamento",
      budgetSetupCapOnlyTitle: "Teto mensal definido — adicione categorias",
      budgetSetupCapOnlyBody:
        "O teto orienta o ritmo de gasto. Distribuir por categoria dá controlo mais claro.",
      budgetSetupCapOnlyCta: "Planear por categoria",
    },
    expenses: {
      newCta: "Nova despesa",
      paid: "Pago",
      unpaid: "Pendente",
      paidBy: "Quem pagou",
      scopeShared: "Casa",
      scopePersonal: "Pessoal",
      category: "Categoria",
      backToJournal: "Voltar ao diário",
      backToExpenseDetail: "Voltar às despesas",
      ledgerMonthTotalLabel: "Pago este mês (somente cobranças liquidadas)",
      ledgerMonthForecastTotalLabel: "Previsão do mês (todas as cobranças)",
      ledgerMemberSharesTitle: "Parcela do valor pago (de divisões)",
      ledgerMemberSharesForecastTitle: "Participação prevista (todas as cobranças)",
      ledgerNoSplitsInMonth:
        "Nenhuma despesa neste mês tem divisão de membros; ",
      listForecastVsActual: "Previsão: {{amount}}",
      actionMarkPaid: "Marcar como pago",
      actionSetActualAmount: "Valor real",
      markPaidConfirmTitle: "Marcar como pago?",
      markPaidConfirmBody:
        "A despesa contará como liquidada para o andamento deste mês. ",
      markPaidConfirmCta: "Sim, marca paga",
      actionPaidZero: "Pago a 0 €",
      paidZeroConfirmTitle: "Liquidar a zero",
      paidZeroConfirmBody:
        "O valor real será guardado como 0 € e a despesa ficará como paga. Serve para meses em que não há saída (p. ex. acumulação bimestral): não conta como caixa neste mês; o pagamento efetivo aparece no mês em que pagar.",
      paidZeroConfirmCta: "Sim, 0 € e paga",
      actualAmountModalTitle: "Valor real da despesa",
      actualAmountModalHint:
        "A previsão permanece conforme planejado; ",
      actualAmountLabel: "Valor real pago",
      actualAmountClear: "Limpar valor real",
      actualAmountSubmit: "Salvar",
      paidFeedbackTitle: "Confirmação",
      paidFeedbackHero: "Despesa marcada como paga!",
      paidFeedbackSubline: "Seu rastreamento está novamente sincronizado.",
      paidFeedbackAmountLabel: "Valor registrado",
      paidFeedbackCategoryLabel: "Categoria",
      paidFeedbackPaidByLabel: "Pago por",
      paidFeedbackDateLabel: "Data",
      paidFeedbackContribution: "{{percent}}% de participação",
      paidFeedbackBackHome: "De volta para casa",
      paidFeedbackViewDetail: "Ver detalhes de despesas",
      form: {
        titleLabel: "Título",
        titlePlaceholder: "por exemplo ",
        titleOptionalHint: "Se estiver vazio e uma categoria for selecionada, o nome da categoria será usado.",
        amountLabel: "Montante total",
        amountHint: "Use vírgula ou ponto para decimais (por exemplo, 24,90).",
        currencyLabel: "Moeda (ISO)",
        dateLabel: "Data",
        categoryLabel: "Categoria",
        categoryNone: "Nenhuma categoria",
        paidByLabel: "Quem pagou",
        paidByDefault: "Você (usuário conectado)",
        paidByJointOption: "Conta conjunta",
        paidByHint:
          "Define com que fundos o gasto é registrado (fundos pessoais, conta conjunta da família ou você por padrão). Não define se o gasto é compartilhado: use « Despesa do agregado » para isso.",
        paymentMethodLabel: "Método de pagamento",
        paymentMethodNone: "Não especificado",
        paymentMethodCard: "Cartão",
        paymentMethodCash: "Dinheiro",
        paymentMethodAccount: "Conta bancária",
        sharedLabel: "Despesa do agregado (partilhada entre os membros)",
        sharedHint:
          "Quando ativado, o rateio soma 100% entre todos os membros da família. Se desativado, será uma despesa pessoal (não partilhada).",
        recurringQuestion: "Esta é uma despesa recorrente?",
        recurringEndsLabel: "Data de término",
        recurringEndsOptionalHelp:
          "Opcional quando o rateio estiver desativado: deixe em branco para séries abertas (serviços públicos, etc.).",
        recurringEndsRequiredWhenProratedHelp:
          "Com frequência mensal e rateio, é necessária uma data de término (dividir o total em N parcelas).",
        recurringEndsProrateOpenPeriodHelp:
          "Você pode deixar a data em branco: o total é o valor de cada período de cobrança (por exemplo, a fatura bimestral) e as cobranças mensais são divididas por esse período.",
        recurringFrequencyLabel: "Freqüência",
        frequencyMonthly: "Mensal",
        frequencyBimonthly: "A cada dois meses",
        frequencyQuarterly: "Trimestral",
        frequencySemiannual: "Semestral",
        frequencyAnnual: "Anual",
        proratedLabel: "Proporcione o total entre as parcelas",
        proratedHint:
          "Quando ativado, o total é dividido entre pagamentos por frequência. ",
        recurringSummary: "Parcelas planejadas: {{count}}. ",
        recurringOpenEndedSummary:
          "Sem data de término: as parcelas são criadas com cerca de {{months}} meses de antecedência; ",
        recurringProratedOpenPeriodPreviewNote:
          "O total é para cada período de faturamento de {{periodMonths}} meses; ",
        submitRecurring: "Ativar despesa recorrente",
        notesLabel: "Notas",
        notesPlaceholder: "Opcional",
        receiptLabel: "Comprovativo (opcional)",
        receiptHelp: "PDF ou imagem (JPEG, PNG, WebP, GIF). Carregamento seguro para o armazenamento.",
        receiptRemove: "Remover ficheiro",
        paidNowLabel: "Registrar como pago agora",
        submitCreate: "Economize despesas",
        submitSave: "Salvar alterações",
        saving: "Salvando…",
        cancelEdit: "Cancelar edição",
        invalidAmount: "Insira um valor válido (0 ou superior).",
        titleOrCategoryRequired: "Insira um título ou escolha uma categoria.",
        recurrenceInvalid: "A data de término precisa ser posterior à data de início e deve haver pelo menos uma cobrança no intervalo.",
        recurringProratedRequiresEndsOn:
          "Ao ratear o total, defina uma data de término da série (um total não pode ser dividido sem o final do período).",
        recurringPreviewNeedAmount:
          "Informe o valor total acima para ver o cálculo das parcelas (série com data de término ou rateio).",
        recurringPreviewNeedAmountMonthlyFixed:
          "Insira o valor de um mês acima para ver o resumo e a divisão estimada.",
        proratedMonthlyAverageHint: "Média aproximada do período: {{amount}}/mês",
        recurrenceKindLegend: "Tipo recorrente",
        recurrenceKindMonthlyFixed:
          "Cobrança mensal fixa (mesmo valor todos os meses; sem necessidade de data de término)",
        recurrenceKindSeries:
          "Séries por frequência (mensal, bimestral…); ",
        seriesSingleChargeHint:
          "Para uma cobrança por período (anual, trimestral, semestral ou bimestral), use Série por frequência com rateio desativado.",
        seriesAnnualPresetAction:
          "Usar preset de assinatura anual (sem rateio)",
        amountLabelMonthly: "Valor por mês",
        amountHintMonthlyFixed:
          "Insira o que você paga em um único mês (por exemplo, hipoteca). ",
        monthlyFixedPatternExplain:
          "Nenhuma data de término é necessária: o diário recebe {{count}} cobranças, uma por mês, cada uma pelo mesmo valor de “um mês” inserido acima (por exemplo, hipoteca mensal). ",
        monthlyFixedHorizonNote:
          "Você verá {{count}} linhas do diário (uma por mês); ",
        smoothBimonthlyBillHint:
          "Se uma conta chega a cada dois meses (por exemplo, água ou lixo) e você deseja reservar metade de cada mês, use esta modalidade e insira metade do valor bimestral como “um mês”.",
        sharedSplitPreviewTitle: "Divisão estimada (este período)",
        sharedSplitPreviewSubtitle:
          "Valor por pessoa da divisão domiciliar. ",
        sharedSplitUsesHousePercent: "Usando porcentagens de membros",
        sharedSplitEqualParts: "Participações iguais entre os membros",
      },
      detail: {
        edit: "Editar",
        markPaid: "Marcar como pago",
        markUnpaid: "Marcar como pendente",
        delete: "Excluir",
        deleteConfirm: "Excluir esta despesa? ",
        paidAtLabel: "Pago em",
        notesTitle: "Notas",
        splitsTitle: "Dividir",
        splitsSubtitle:
          "Valor para cada pessoa com base nos percentuais armazenados nesta despesa.",
        splitShare: "{{share}}%",
        notApplicable: "-",
        scopeLabel: "Âmbito",
        paymentMethod: "Método de pagamento",
        receiptLabel: "Comprovativo",
        receiptView: "Ver comprovativo",
        receiptNotAttached: "Sem comprovativo anexado",
        receiptReplaceHelp:
          "Pode carregar um novo comprovativo para substituir o atual sem sair deste ecrã.",
        seriesLink: "Ver séries recorrentes",
        seriesFutureInstallmentLabel:
          "Valor por parcela (atualiza encargos futuros não pagos)",
        seriesSave: "Salvar alterações na série",
        seriesLimitSectionTitle: "Encurtar ou terminar",
        seriesLimitSectionIntro:
          "Defina a data da última cobrança ou encerre a série agora. ",
        seriesEndsOnLabel: "Última cobrança (data de término)",
        seriesEndsOnHelp:
          "As parcelas não pagas datadas após esta data serão canceladas. ",
        seriesApplyEndsOn: "Salvar data de término",
        seriesEndNow: "Terminar a série agora",
        seriesEndNowConfirm:
          "As futuras parcelas não pagas serão canceladas e a série deixará de gerar cobranças. ",
        seriesExtendSectionTitle: "Estender a série",
        seriesExtendIntroActiveWithEnd:
          "Retirar a data de término para que as parcelas sejam geradas novamente até o horizonte do sistema. ",
        seriesExtendIntroEnded:
          "Reative a série e remova a data de término. ",
        seriesRemoveEndsOn: "Remover data de término",
        seriesRemoveEndsOnConfirm:
          "Remover a data de término? ",
        seriesReactivateOpenEnded: "Reativar sem data de término",
        seriesReactivateOpenEndedConfirm:
          "Reativar a série e remover a data de término? ",
        seriesReactivateOnly: "Reativar série",
        seriesReactivateOnlyConfirm:
          "Reativar a série? ",
        seriesExtendIntroEndedNoEnd:
          "Esta série terminou e não tem data de término registrada. ",
        seriesStatusBadgeActive: "Ativo",
        seriesStatusBadgePaused: "Pausado",
        seriesStatusBadgeEnded: "Terminou",
        seriesEndedReadOnly:
          "Esta série terminou. ",
        seriesInvalidEndDate: "Insira uma data válida (AAAA-MM-DD).",
        budgetSetupBannerTitle: "Sem referência de orçamento neste mês",
        budgetSetupBannerBody:
          "O mês desta despesa não tem plano por categorias nem teto mensal da casa. Configure em Orçamento.",
        budgetSetupBannerCta: "Abrir orçamento",
      },
      filters: {
        title: "Filtros",
        searchPlaceholder: "Pesquise no diário…",
        scopeLabel: "Âmbito",
        scopeAll: "Todos",
        scopeShared: "Casa",
        scopePersonal: "Pessoal",
        categoryAll: "Todas as categorias",
        paidAll: "Todos",
        paidYes: "Pago apenas",
        paidNo: "Somente não pago",
        apply: "Aplicar",
        clear: "Claro",
      },
      flows: {
        launcherTitle: "O que pretende fazer?",
        launcherSubtitle:
          "Escolha um percurso conforme o tipo de despesa que pretende registar.",
        manualOnlyBanner:
          "Introduza os dados principais da despesa: valor, data e método de pagamento.",
        intentQuickTitle: "Registar uma despesa pontual",
        intentQuickDescription:
          "Para um pagamento que já fez: montante, categoria e como pagou. O resto só se precisar.",
        intentRecurringTitle: "Despesa fixa todos os meses",
        intentRecurringDescription:
          "Uma prestação que se repete (renda, condomínio…). Um assistente passo a passo explica o que está a configurar.",
        intentSeriesTitle: "Série com datas ou rateio",
        intentSeriesDescription:
          "Prestações com data de fim, outras frequências ou repartição de um total por meses. Resumo antes de guardar.",
        quickExpandMore: "Mais opções (título, notas, partilhado…)",
        repeatLastFlowCta: "Continuar com: {{flowLabel}}",
        navNext: "Seguinte",
        navBack: "Voltar",
        navReview: "Ver resumo",
        editStepA11y: "Editar passo {{step}}",
        monthlyWizard: {
          step1Title: "Que despesa é?",
          step1Help: "Título ou categoria para a reconhecer todos os meses no diário.",
          step2Title: "Quanto paga por mês?",
          step2Help: "Um único montante mensal. Repetimos em cada prestação.",
          step3Title: "Quando começa?",
          step3Help: "Data do primeiro débito ou do dia em que deve aparecer no diário.",
          step4Title: "Como paga?",
          step4Help: "Cartão, numerário ou conta: indica como sai o dinheiro (tudo manual).",
          step5Title: "É partilhada em casa?",
          step5Help: "Quem paga e se a despesa é partilhada entre membros.",
          reviewTitle: "Assim ficará a sua despesa fixa",
          reviewHelp: "Revise os dados. Pode voltar atrás e alterar qualquer passo.",
        },
        seriesWizard: {
          step1Title: "Título e montante",
          step1Help:
            "O que é esta despesa e o montante conforme o modo (por mês ou total do período, conforme escolher a seguir).",
          step2Title: "Com que frequência se repete",
          step2Help: "Mensal, trimestral, anual… A frequência define as linhas no diário.",
          step3Title: "Início e fim",
          step3Help:
            "Data de início e, se aplicável, última prestação. Sem data de fim, a série pode ficar aberta conforme as regras do formulário.",
          step4Title: "Rateio (prorata)",
          step4Help:
            "Se reparte um total por várias prestações, aqui se explica a divisão. Sem banco: confirme os montantes.",
          step5Title: "Reparto entre pessoas",
          step5Help: "Partilhado e quem pagou. Verá uma pré-visualização quando montante e datas estiverem prontos.",
          reviewTitle: "Resumo da série",
          reviewHelp: "Confirme prestações e datas antes de criar a série.",
        },
      },
    },
    financings: {
      pageTitle: "Financiamento",
      newCta: "Novo financiamento",
      emptyTitle: "Ainda não há financiamento",
      emptyHint:
        "Registre empréstimos ou parcelas fixas com data de início do contrato. ",
      stepDataTitle: "Detalhes",
      stepReviewTitle: "Análise",
      titleLabel: "Nome",
      lenderLabel: "Credor ou instituição",
      installmentLabel: "Parcela mensal",
      contractStartLabel: "Início do contrato (calendário de parcelamento)",
      endsOnLabel: "Data da última parcela",
      notesLabel: "Notas",
      docsHint: "Documentos opcionais (PDF ou imagem). ",
      runAiLabel: "Analise documentos com IA",
      runAiHint:
        "Resume os termos-chave apenas para fins informativos. ",
      generateRecurringLabel: "Gerar despesa recorrente mensal",
      generateRecurringHint:
        "Cria uma série de diário a partir da primeira parcela de hoje ou depois (UTC) até a data de término.",
      reviewCutoff: "Data limite: {{date}} (UTC)",
      reviewFirstPayment: "Primeira parcela do diário: {{date}}",
      reviewFutureCount: "Parcelas futuras: {{count}}",
      reviewPreviewDates: "Próximas datas (amostra)",
      reviewWillSeries: "Uma série de despesas recorrentes será criada",
      reviewWillAi: "O primeiro documento será enviado para AI após salvar",
      reviewNoSeries: "Nenhuma despesa automática será criada",
      backCta: "Voltar",
      continueCta: "Continuar a revisar",
      confirmCta: "Confirme e salve",
      saving: "Salvando…",
      aiDisclaimer: "Os resumos de IA são indicativos e podem conter erros.",
      aiSkipped: "IA não usada",
      aiPending: "Resumo de IA pendente",
      aiProcessing: "Analisando documentos…",
      aiCompleted: "Resumo de IA pronto",
      aiFailed: "Falha no resumo da IA",
      viewSeriesCta: "Ver série de despesas",
      listInstallment: "Parcela: {{amount}}",
      listTermProgress: "{{percent}}% do prazo",
      listContractEndedBadge: "Terminou",
      listInstallmentsSummary:
        "{{past}} de {{total}} parcelas decorridas · {{remaining}} restantes",
      listEstimatedRemaining: "Pendente estimado: {{amount}}",
      listNoInstallmentSchedule: "Sem cronograma de parcelamento",
      categoryLabel: "Categoria",
      categoryNone: "Nenhuma categoria",
      openDocument: "Abrir documento",
      summaryTitle: "Resumo de IA",
      keyTermsTitle: "Termos-chave",
      editCta: "Editar",
      deleteCta: "Excluir",
      deleteConfirm:
        "Excluir este financiamento? ",
      saveCta: "Salvar alterações",
      saveSuccess: "Financiamento atualizado.",
    },
    budget: {
      heroEyebrow: "Alocação do lar",
      heroTitle: "Gasto do lar planejado este mês",
      heroSubtitle:
        "Total das rubricas orçamentárias do lar para este período. As despesas pessoais são registradas separadamente no diário.",
      goalLabel: "Progresso da meta: {{yearMonth}}",
      percentComplete: "{{percent}}% concluído",
      reservedLabel: "{{amount}} gasto do lar",
      targetLabel: "{{amount}} destino",
      categoryGridTitle: "Por categoria",
      plannedLabel: "Planejado",
      spentLabel: "Gasto do lar",
      plannedInputHint: "Valor planejado (unidades principais)",
      monthNav: "Mês",
      prevMonth: "Mês anterior",
      nextMonth: "Próximo mês",
      saveCta: "Economize orçamento",
      saving: "Salvando…",
      saveSuccess: "Orçamento atualizado.",
      copyFromPreviousConfirmTitle: "Copiar do mês anterior?",
      copyFromPreviousConfirmMessage:
        "As linhas de orçamento do mês anterior serão aplicadas a este mês. Depois pode ajustá-las.",
      copyFromPreviousCta: "Importar do mês anterior",
      readOnlyHint: "Somente proprietários e administradores podem editar o orçamento.",
      performanceTitle: "Desempenho do período",
      performanceSubtitle: "Orçamento planejado do lar versus gastos reais do lar neste mês.",
      legendBudgeted: "Planejado do lar",
      legendSpent: "Gasto do lar",
      noCategoriesHint:
        "Nenhuma categoria ainda. ",
      addCategoryCard: "Adicionar categoria",
      addCategoryCardHint: "Novas categorias podem ser selecionadas ao criar uma despesa.",
      addCategoryFormTitle: "Nova categoria",
      addCategoryNameLabel: "Nome",
      addCategoryNamePlaceholder: "por exemplo ",
      addCategorySubmit: "Criar",
      addCategoryCancel: "Cancelar",
      addCategoryCreating: "Criando…",
      heroVsPrevUp: "+{{pct}}% em relação ao mês passado",
      heroVsPrevDown: "−{{pct}}% em relação ao mês passado",
      heroVsPrevFlat: "Igual ao mês passado",
      proratedTitle: "Obrigações rateadas",
      proratedLinkJournal: "Ver diário",
      proratedReserveHint: "Parcela mensal estimada",
      proratedTotalBadge: "Total {{amount}}",
      proratedEmpty:
        "Séries com total rateado (IPTU, seguro anual, etc.) serão exibidas aqui. ",
      planningSectionTitle: "Planejamento de categoria",
      bannerEyebrow: "Hábitos sustentáveis",
      bannerTitle: "Um orçamento claro reduz surpresas e alinha sua família com o que é importante.",
      badgeOverBudget: "Acima do plano",
      badgeUnderBudget: "Sob plano",
      badgeOnTrack: "No caminho certo",
      badgeNoPlan: "Sem plano",
      columnBudget: "Orçamento",
      columnReal: "Real",
      analysisEmpty:
        "Ainda não há categorias com plano ou gastos neste mês. ",
      monthlyCapHint: "Limite mensal doméstico",
      moneySnapshotTitle: "Resumo rápido de caixa",
      moneySnapshotOpening: "Saldo inicial",
      moneySnapshotNeedMonth: "Necessário neste mês",
      moneySnapshotRetained: "Retido (rateados)",
      moneySnapshotAvailableAfterRetained: "Disponível após retenções",
      moneySnapshotProjectedBalance: "Saldo estimado da conta",
    },
    vault: {
      pageTitle: "Cofre",
      pageSubtitle:
        "Gerencie com segurança garantias e políticas residenciais. ",
      emptyHint: "Registre garantias, políticas e ativos com datas importantes.",
      addCta: "Adicionar recurso",
      nameLabel: "Nome do item",
      namePlaceholder: "por exemplo ",
      assetTypeLabel: "Categoria",
      assetTypePlaceholder: "por exemplo ",
      purchaseDateLabel: "Data de compra",
      warrantyLabel: "A garantia termina",
      notesLabel: "Notas",
      notesPlaceholder: "Detalhes do contrato, referências…",
      notesOptionalHint: "Opcional",
      documentKeyLabel: "Chave do documento (armazenamento)",
      documentKeyPlaceholder: "Depois de enviar um arquivo para armazenamento",
      saveCta: "Salvar",
      saving: "Salvando…",
      createCta: "Salvar no cofre",
      backToList: "De volta ao cofre",
      deleteCta: "Excluir",
      deleteConfirm: "Remover este item do cofre?",
      readOnlyHint: "Somente proprietários e administradores podem criar ou editar itens.",
      cardWarranty: "Garantia até {{date}}",
      cardPurchased: "Comprado {{date}}",
      formKicker: "Cadastro",
      formTitle: "Novo recurso do cofre",
      formSubtitle: "Proteja sua compra com detalhes originais e comprovante de compra.",
      priceLabel: "Preço/valor",
      pricePlaceholder: "0,00",
      priceInNotesLine: "Valor estimado: {{amount}}",
      documentUploadTitle: "Recibo/fatura",
      documentUploadHint: "Carregar comprovante de compra",
      documentUploadSub: "Toque para escolher um arquivo ou imagem",
      documentUploadedHint: "Documento anexado",
      completionLabel: "Progresso do registro",
      saveLegalNote:
        "Ao salvar, você autoriza o armazenamento seguro na nuvem de seus documentos de compra e datas de garantia.",
      filterAll: "Todos",
      filterEmpty: "Nenhum item nesta categoria.",
      alertKicker: "Alerta",
      alertTitle: "Atenção de garantia",
      alertDaysLeft: "A garantia “{{name}}” expira em {{days}} dias.",
      alertExpired: "A garantia para “{{name}}” expirou. ",
      alertViewDetails: "Ver detalhes",
      statTotalLabel: "Ativos do cofre",
      statSecuredLabel: "Com data de garantia",
      statSecuredLine: "Itens com data de término da garantia atual ou futura",
      sectionCount: "{{count}} itens",
      cardPurchaseDate: "Data de compra",
      cardExpiryDate: "A garantia termina",
      viewDocument: "Ver recibo",
      registerNewItem: "Registrar novo item",
      warrantyBadgeExpired: "Expirado",
      warrantyBadgeUrgent: "Urgente",
      warrantyBadgeWarning: "Breve",
      warrantyBadgeSafe: "OK",
      warrantyBadgeNone: "Sem data",
      warrantyEndsToday: "Termina hoje",
      categoryPlaceholder: "Escolha a categoria",
      sectionUncategorized: "Sem categoria",
      categories: {
        appliances: "Eletrodomésticos",
        electronics: "Eletrônica",
        furniture: "Mobília",
        vehicles: "Veículos",
        other: "Outro",
      },
    },
    scan: {
      overlayTitle: "Varredura de IA",
      overlaySubtitle: "Carregue uma foto do recibo; ",
      pickFile: "Escolha a imagem",
      uploading: "Fazendo upload…",
      registering: "Registrando digitalização…",
      statusPending: "Na fila",
      statusProcessing: "Processamento",
      statusCompleted: "Concluído",
      statusFailed: "Fracassado",
      extractedTitle: "Dados detectados",
      rawPayloadHint: "Visualização do resultado bruto (JSON).",
      recentTitle: "Verificações recentes",
      linkExpenseHint: "Revise os dados extraídos e crie a despesa no diário quando a verificação for concluída.",
      pollHint: "Atualizando status…",
      createExpenseFromScan: "Crie despesas com esses dados",
      fromScanBanner: "Campos pré-preenchidos em uma varredura de IA. ",
      extractionErrorFallback: "Não foi possível interpretar o recibo.",
      extractedMerchant: "Comerciante",
      extractedDate: "Data",
      extractedTotal: "Total",
    },
    profile: {
      pageTitle: "Perfil",
      accountSettings: "Conta e dados",
      accountSettingsHint: "Preferências pessoais",
      notifications: "Notificações",
      notificationsHint: "Push, preferências e dispositivos",
      devices: "Dispositivos e sessões",
      devicesHint: "Veja onde você está conectado e saia remotamente",
      privacy: "Privacidade e segurança",
      privacyHint: "Senha e permissões",
      help: "Ajuda e suporte",
      helpHint: "Perguntas frequentes e contato",
      logout: "Sair",
      logoutHint: "Saia com segurança",
      footerNote: "HomeExpense",
      profileFormSectionTitle: "Perfil e idioma",
      nameField: "Nome de exibição",
      saveButton: "Salvar alterações",
      saving: "Salvando…",
      savedHint: "Alterações salvas.",
      clearAvatarButton: "Remover imagem",
      avatarChangeAriaLabel: "Alterar foto do perfil",
      avatarUploading: "Fazendo upload da imagem…",
      avatarUploadError: "Não foi possível fazer upload da imagem. ",
    },
    personalDataPage: {
      subtitle: "Email, idioma da interface e nome de exibição.",
      emailLabel: "E-mail",
      emailHint: "E-mail vinculado à sua conta. Não pode ser editado aqui.",
      deleteAccountSectionTitle: "Eliminar conta",
      deleteAccountSectionHint:
        "Elimina permanentemente o seu utilizador, sessões e dados pessoais. Em lares partilhados, outro membro passará a constar como criador dos registos que permanecerem.",
      deleteAccountButton: "Eliminar a minha conta e os meus dados",
      deleteAccountModalTitle: "Eliminar a conta permanentemente?",
      deleteAccountModalBody:
        "Esta ação não pode ser anulada. Perderá o acesso ao HomeExpense e os dados da conta serão removidos. Se partilhava um lar, os outros membros mantêm o lar e o histórico é reatribuído.",
      deleteAccountModalConfirm: "Sim, eliminar a minha conta",
      deleteAccountModalCancel: "Cancelar",
      deleteAccountError: "Não foi possível concluir a eliminação. Tente novamente ou contacte o suporte.",
      deleteAccountPending: "A eliminar a conta…",
    },
    settingsHub: {
      unifiedPageTitle: "Conta e dados",
      unifiedSubtitle: "Foto do perfil e links para detalhes pessoais, segurança e ajuda.",
      backToAccount: "Voltar para conta e dados",
      cardPersonalDataTitle: "Dados pessoais",
      cardPersonalDataHint: "Idioma, nome de exibição e e-mail",
      cardPasswordTitle: "Senha",
      cardPasswordHint: "Alterar senha enquanto estiver conectado",
      cardPasskeysTitle: "Passkeys",
      cardPasskeysHint: "Adicionar ou gerir início de sessão sem palavra-passe neste navegador",
      cardNotificationsTitle: "Notificações",
      cardNotificationsHint: "Tipos de push e alerta de dispositivo",
      cardDevicesTitle: "Dispositivos e sessões",
      cardDevicesHint: "Sessões ativas e saída remota",
      cardSupportTitle: "Ajuda e suporte",
      cardSupportHint: "Recuperar acesso e perguntas frequentes",
      cardDeleteAccountTitle: "Eliminar conta",
      cardDeleteAccountHint: "Remover permanentemente o seu utilizador e dados associados",
    },
    settingsPasswordForm: {
      subtitle: "Digite sua senha atual e uma nova. ",
      currentPassword: "Senha atual",
      newPassword: "Nova Senha",
      confirmPassword: "Confirme a nova senha",
      submit: "Atualizar senha",
      successMessage: "Senha atualizada com sucesso.",
      forgotPasswordCta: "Esqueci minha senha",
      noCredentialHint: "Nenhuma senha está definida para esta conta. ",
      changeAgain: "Mudar novamente",
    },
    settingsPasskeysPage: {
      subtitle: "Registe uma passkey neste dispositivo para um início de sessão mais rápido da próxima vez.",
      disabledHint: "As passkeys estão desativadas neste ambiente (VITE_PASSKEY_AUTH_ENABLED).",
      addHint: "Utilize um autenticador de plataforma (p. ex. Touch ID, Windows Hello ou chave do sistema).",
      addButton: "Adicionar passkey",
      adding: "A registar…",
      loading: "A carregar passkeys…",
      loadError: "Não foi possível carregar as passkeys. Tente novamente.",
      empty: "Ainda não tem passkeys registadas nesta conta.",
      unnamedPasskey: "Passkey sem nome",
      deviceTypeLabel: "Tipo: {{type}}",
      createdLabel: "Criada:",
      renameButton: "Renomear",
      renameDialogTitle: "Nome da passkey",
      renameLabel: "Nome visível",
      renameSave: "Guardar",
      renameEmpty: "Indique um nome para a passkey.",
      deleteButton: "Eliminar",
      deleteConfirm: "Eliminar esta passkey? Deixará de a poder usar para iniciar sessão.",
      deleting: "A eliminar…",
    },
    supportPage: {
      subtitle: "Recursos e links úteis.",
      intro: "Ajuda para acesso à conta e links relacionados à segurança.",
      forgotTitle: "Esqueceu sua senha?",
      forgotBody: "Enviamos a você por e-mail um link para redefini-la sem saber a senha antiga.",
      forgotCta: "Vá para recuperar o acesso",
    },
    notificationsPage: {
      subtitle: "Alertas e atualizações para sua casa.",
      emptyTitle: "Sem notificações",
      emptyHint: "Quando houver atualizações (convites, lembretes, etc.), elas aparecerão aqui.",
      inboxTitle: "Caixa de entrada",
      pushSectionTitle: "Notificações push",
      activatePush: "Ativar neste navegador",
      pushWorking: "Push está ativo neste dispositivo.",
      permissionDenied: "Permissão de notificação negada. ",
      notSupported: "Este navegador não suporta web push.",
      configMissing: "O Firebase não está configurado (faltando VITE_FIREBASE_* env vars).",
      registerError: "Não foi possível registrar este dispositivo para push.",
      devicesSectionTitle: "Dispositivos registrados",
      noDevices: "Nenhum dispositivo ainda. ",
      platformLabel: "Plataforma",
      platformWeb: "Rede",
      platformIos: "iOS",
      platformAndroid: "Android",
      lastSeenLabel: "Última atividade",
      removeDevice: "Remover dispositivo",
      prefsSectionTitle: "Preferências de notificação",
      prefPushMaster: "Receba notificações push",
      prefHouseInvites: "Casa e convites",
      prefExpenses: "Despesas e diário",
      prefBudget: "Orçamento e limites",
      prefReminders: "Lembretes",
      prefScanDocs: "Digitalizações e documentos",
      prefDigests: "Resumos",
      prefUpcomingCharges: "Cobranças com vencimento amanhã (não pagas)",
      markAllRead: "Marcar tudo como lido",
      markOneRead: "Marcar como lido",
      loadMore: "Carregar mais",
      unreadLabel: "Não lido",
      viewAction: "Abrir",
      removeDeviceConfirm: "Remover este dispositivo das notificações push?",
      devicePushLabel: "Empurre este dispositivo",
    },
    pendingInvitationsPage: {
      title: "Convites pendentes",
      subtitle:
        "Aceite convites para ingressar nas casas aqui. ",
      emptyTitle: "Nenhum convite pendente",
      emptyHint:
        "Se você foi convidado por e-mail, verifique o link na mensagem. ",
      inviterLine: "Convidado por: {{inviterName}}",
      expiresLabel: "Expira em {{date}}",
      acceptCta: "Aceite e participe",
      accepting: "Juntando-se…",
      loadError: "Não foi possível carregar os convites. ",
    },
    notificationSettingsPage: {
      subtitle:
        "Ative as notificações do navegador, escolha quais alertas você receberá e gerencie os dispositivos registrados.",
    },
    notificationsInApp: {
      pushDeviceRegisteredTitle: "Dispositivo de notificação",
      pushDeviceRegisteredBody: "Este dispositivo ({{platform}}) foi registrado com sucesso.",
      receiptScanCompletedTitle: "Recibo pronto para revisão",
      receiptScanCompletedBody:
        "A verificação de {{houseName}} foi concluída. ",
      receiptScanFailedTitle: "Não foi possível ler o recibo",
      receiptScanFailedBody:
        "A verificação de {{houseName}} falhou. ",
      houseInvitationReceivedTitle: "Convite para casa",
      houseInvitationReceivedBody:
        "{{inviterName}} convidou você para participar de «{{houseName}}». ",
      houseInvitationAcceptedTitle: "Alguém se juntou à casa",
      houseInvitationAcceptedBody:
        "{{inviteeName}} aceitou o convite e agora faz parte de «{{houseName}}».",
      budgetThreshold80Title: "Orçamento em {{percent}}%",
      budgetThreshold80Body:
        "Em «{{houseName}}», os gastos pagos para {{yearMonth}} atingiram {{percent}}% da meta.",
      budgetThreshold100Title: "Orçamento no limite",
      budgetThreshold100Body:
        "Em «{{houseName}}», os gastos pagos para {{yearMonth}} atingiram {{percent}}% da meta.",
      budgetMonthStartedTitle: "Novo mês em {{houseName}}",
      budgetMonthStartedBody: "{{yearMonth}} foi iniciado. ",
      budgetMonthNeedsSetupTitle: "Orçamento pendente em {{houseName}}",
      budgetMonthNeedsSetupBody:
        "{{yearMonth}} começou e não havia plano anterior para copiar. Defina linhas por categoria em Orçamento ou um teto mensal da casa como alternativa.",
      budgetMonthSeededFromPreviousTitle: "Orçamento preparado em {{houseName}}",
      budgetMonthSeededFromPreviousBody:
        "{{yearMonth}}: o orçamento por categorias já está guardado na sua casa ({{copiedCount}} linhas de {{sourceYearMonth}}). Abra Orçamento só para rever ou ajustar valores se precisar.",
      vaultWarrantyExpiring7dTitle: "Garantia terminando em breve",
      vaultWarrantyExpiring7dBody:
        "«{{itemName}}» em «{{houseName}}» tem garantia até {{endDate}} (faltam cerca de 7 dias).",
      upcomingExpenseTomorrowTitle: "Cobranças com vencimento amanhã em {{houseName}}",
      upcomingExpenseTomorrowBody:
        "Você tem {{count}} despesas não pagas datadas de {{tomorrowDate}}. ",
    },
    devicesPage: {
      subtitle: "Controle onde você está conectado.",
      intro:
        "Cada linha é uma sessão ativa. ",
      thisDevice: "Este dispositivo",
      currentBadge: "Atual",
      lastActive: "Última atividade",
      sessionStarted: "Conectado",
      expires: "Expira",
      ip: "PI",
      browser: "Navegador ou aplicativo",
      unknownBrowser: "Desconhecido",
      signOutDevice: "Sair deste dispositivo",
      signOutDeviceConfirm:
        "Sair deste dispositivo? ",
      signOutOthers: "Sair de qualquer outro lugar",
      signOutOthersConfirm:
        "Sair de todos os outros dispositivos? ",
      loadError: "Não foi possível carregar as sessões.",
      actionError: "A ação não pôde ser concluída. ",
      empty: "Nenhuma sessão ativa.",
    },
  },
  mobile: {
    common: {
      cancel: "Cancelar",
      confirm: "Confirmar",
      skip: "Pular",
      ok: "OK",
      changesSaved: "Alterações salvas.",
      retry: "Tentar novamente",
      loadError: "Não foi possível carregar os dados.",
      signingOut: "Saindo…",
      signOutConfirmTitle: "sair",
      signOutConfirmMessage: "Tem certeza de que deseja sair deste dispositivo?",
      signOutFailedTitle: "Não foi possível sair",
      signOutFailed:
        "Verifique sua conexão e tente novamente. ",
    },
    session: {
      checking: "Verificando sessão…",
    },
    api: {
      errors: {
        network: "Nenhuma conexão ou o servidor não está respondendo. ",
        generic: "A operação não pôde ser concluída. ",
        serverUnavailable: "O serviço está temporariamente indisponível. ",
        missingConfig: "O URL da API não está configurado. ",
      },
    },
    tabs: {
      home: "Lar",
      houses: "Casas",
      scan: "Digitalizar",
      budget: "Orçamento",
      vault: "Cofre",
    },
    shell: {
      comingSoon:
        "Esta seção estará disponível em breve. ",
    },
    home: {
      title: "HomeExpense",
      subtitle: "Seu espaço financeiro doméstico, com clareza do Estate Ledger.",
      heroEyebrow: "ABERTURA DE SALDO BANCÁRIO",
      heroTitle: "Bem-vindo",
      heroSubtitle: "Gerencie despesas e orçamento com calma e foco editorial.",
      heroTrend: "Sincronizado com a API",
      heroRemainingLabel: "{{amount}} estimativa restante",
      openSheet: "Abra a folha inferior",
      openPaginatedDemo: "Notificações (API)",
      healthChecking: "API: verificando…",
      healthOk: "API: on-line",
      healthFail: "API: inacessível",
      insightTitle: "Dentro do alcance",
      insightBody:
        "Seu ritmo de gastos neste mês está estável. ",
      insightBadge: "Eficiência",
      insightValue: "Excelente",
      insightValueGood: "Excelente",
      insightValueFair: "No caminho certo",
      insightValueOver: "Excedido",
      upcomingCardBadge: "Próxima despesa",
      upcomingCardBodyGeneric: "Cobrança esperada neste período.",
      upcomingCardNone: "Sem despesas pendentes",
      budgetCardTitle: "Orçamento mensal",
      budgetCardCompleted: "{{percent}}% usado",
      budgetCardTotal: "Total: {{amount}}",
      monthSummaryTitle: "Resumo do mês",
      monthSummaryPersonalLabel: "Pessoal",
      monthSummarySharedLabel: "Comum",
      monthSummaryTotalLabel: "Total a contribuir",
      monthSummaryForecastPrefix: "Previsto: {{amount}}",
      houseSectionTitle: "Orçamento da casa",
      personalSectionTitle: "Gasto pessoal",
      quickActionsTitle: "Ações rápidas",
      quickExpense: "Despesa de registro",
      quickScan: "Digitalizar recibo",
      quickMembers: "Membros",
      quickFinancings: "Financiamentos",
      quickFabA11y: "Abra o menu de ações rápidas",
      quickFabCloseA11y: "Fechar menu de ações rápidas",
      quickFabLabel: "Ações",
      quickFabCloseLabel: "Fechar",
      openJournal: "Diário aberto",
      openJournalHeaderA11y: "Abrir diário",
      financingsShortcutTitle: "Financiamentos",
      financingsShortcutSubtitle: "Empréstimos e parcelamentos para sua casa ativa.",
      analyticsShortcutTitle: "Informações sobre a família",
      analyticsShortcutSubtitle: "Gráficos para sinais pagos versus planejados e de categoria.",
      openingBalanceLabel: "Saldo inicial do mês",
      openingBalanceMock: "2.450,00€",
      defaultHouseName: "Casa principal",
      notificationsA11y: "Notificações",
      profileA11y: "Perfil",
      houseSelectorA11y: "Selecione a página inicial ativa",
      noHouseTitle: "Ainda não há casas",
      noHouseBody:
        "Crie ou participe de uma casa na web para sincronizar despesas, orçamento e dados do cofre aqui.",
      chooseHouseTitle: "Casa ativa",
      recentTitle: "Próximas despesas",
      upcomingViewAll: "Ver todas",
      goToChangePassword: "Alterar a senha",
      budgetSetupUrgentTitle: "Ainda sem referência de orçamento do mês",
      budgetSetupUrgentBody:
        "Sem linhas por categoria nem teto mensal da casa, o progresso é difícil de ler. Configure em Orçamento.",
      budgetSetupUrgentCta: "Abrir orçamento",
      budgetSetupCapOnlyTitle: "Teto mensal definido — adicione categorias",
      budgetSetupCapOnlyBody:
        "O teto orienta o ritmo de gasto. Distribuir por categoria dá controlo mais claro.",
      budgetSetupCapOnlyCta: "Planear por categoria",
    },
    journal: {
      title: "Diário financeiro",
      searchPlaceholder: "Pesquise no diário…",
      fabA11y: "Adicionar despesa",
      fabLabel: "Adicionar despesa",
      filterAll: "Todos",
      filterHousehold: "Casa",
      filterPersonal: "Pessoal",
      filterGroceries: "Mantimentos",
      filterUtilities: "Utilitários",
      filterInsurance: "Seguro",
      groupMonth: "Outubro de 2025",
      expense1Title: "Loja semanal",
      expense2Title: "Eletricidade",
      expense1Meta: "12 de outubro ·",
      expense2Meta: "8 de outubro ·",
      expense1Amount: "142,50€",
      expense2Amount: "89,20€",
      memberYou: "Você",
      badgePaid: "Pago",
      badgeUnpaid: "Pendente",
      empty: "Nenhuma despesa corresponde à sua pesquisa ou filtro.",
      emptyTitle: "Ainda não há despesas",
      emptyDescription:
        "Registre compras e recibos para ver seu histórico por mês e ficar por dentro dos gastos da família.",
      emptyFilteredTitle: "Nenhum resultado",
      emptyCta: "Adicionar despesa",
    },
    expenseQuickActions: {
      menuA11y: "Mais ações para esta despesa",
      markPaidChip: "Marcar como pago",
      adjustActualChip: "Valor real",
      paidZeroChip: "Pago a 0€",
      paidZeroConfirmTitle: "Acomode-se em zero",
      paidZeroConfirmMessage:
        "O valor real será salvo como 0 e a despesa será marcada como paga. ",
      paidZeroSuccess: "Despesa liquidada em 0 e marcada como paga.",
      actualAmountPlaceholder: "0,00",
    },
    expenseDetail: {
      screenTitle: "Detalhes de despesas",
      totalAmountLabel: "MONTANTE TOTAL",
      dateLabel: "Data",
      categoryLabel: "Categoria",
      scopeLabel: "Âmbito",
      paymentMethodLabel: "Método de pagamento",
      notesLabel: "Notas",
      paidByLabel: "Pago por",
      recurringLabel: "Recorrência",
      proratedLabel: "Mensalmente rateado",
      splitsTitle: "DISTRIBUIÇÃO DE CUSTOS",
      splitPercent: "{{percent}} % de contribuição",
      markAsPaidCta: "Marcar como pago",
      markAsUnpaidCta: "Marcar como pendente",
      markingPaid: "Marcando…",
      markingUnpaid: "Desfazendo…",
      markPaidSuccess: "Despesa marcada como paga.",
      markUnpaidSuccess: "Despesa marcada como pendente.",
      editCta: "Editar",
      noCategoryLabel: "Nenhuma categoria",
      noNotesLabel: "Sem notas",
      confirmMarkPaid: "Confirmar pagamento",
      confirmMarkPaidMessage: "Marcar esta despesa como paga?",
      confirmMarkUnpaid: "Desfazer pagamento",
      confirmMarkUnpaidMessage: "Marcar esta despesa como pendente novamente?",
      errorMarkPaid: "Não foi possível marcar como pago. ",
      errorMarkUnpaid: "Não foi possível desfazer o pagamento. ",
      cycleLabel: "Ciclo",
      projectionLabel: "Projeção",
      perMonth: "mo",
      actualCostLabel: "Custo real",
      realCostLabel: "Valor real registrado",
      updateCostTitle: "Atualizar custo real",
      updateCostCta: "Custo de atualização",
      updateCostError: "Não foi possível atualizar o custo. ",
      updateCostSuccess: "Custo real atualizado.",
      receiptLabel: "Comprovativo",
      receiptAttached: "Comprovativo anexado",
      receiptNotAttached: "Sem comprovativo anexado",
      receiptViewCta: "Ver comprovativo",
      receiptAttachCta: "Anexar comprovativo",
      receiptReplaceCta: "Substituir comprovativo",
      receiptSourceTitle: "Anexar comprovativo",
      receiptSourceMessage: "Escolha como quer anexar o comprovativo.",
      receiptSourceCamera: "Tirar foto",
      receiptSourceGallery: "Escolher da galeria",
      receiptSourceDocument: "Escolher documento",
      receiptUpdating: "A carregar comprovativo…",
      receiptUpdatedSuccess: "Comprovativo atualizado.",
      receiptUpdateError:
        "Não foi possível atualizar o comprovativo. Tente novamente.",
      deleteCta: "Excluir despesa",
      deleteConfirmTitle: "Excluir despesa",
      deleteConfirmMessage: "Excluir esta despesa? ",
      deleteError: "Não foi possível excluir a despesa. ",
      deleteSuccess: "Despesa excluída.",
      deleting: "Excluindo…",
      freqMonthly: "Mensal",
      freqBimonthly: "Bimestralmente",
      freqQuarterly: "Trimestral",
      freqSemiannual: "Semestral",
      freqAnnual: "Anual",
      methodCard: "Cartão",
      methodCash: "Dinheiro",
      methodAccount: "Conta",
      budgetSetupBannerTitle: "Sem referência de orçamento neste mês",
      budgetSetupBannerBody:
        "Este mês não tem plano por categorias nem teto mensal da casa. Configure em Orçamento.",
      budgetSetupBannerCta: "Abrir orçamento",
    },
    expenseSeries: {
      screenTitle: "Série recorrente",
    },
    houses: {
      screenTitle: "Casas e membros",
      subtitle: "Escolha uma casa ativa para ver as despesas e o orçamento vinculados.",
      managementEyebrow: "Gerenciamento",
      heroDescription:
        "Organize suas propriedades e gerencie com precisão a distribuição das despesas entre os moradores.",
      sampleHouseName: "Casa principal",
      activeBadge: "Ativo",
      membersCta: "Membros e convites",
      budgetHint: "Orçamento mensal em 68%",
      budgetHintLive: "Progresso do mês: {{percent}}% · gastos até o momento {{spent}}",
      empty: "Você ainda não tem casa. ",
      emptyTitle: "Ainda não há casas",
      emptyDescription:
        "Crie sua primeira casa para sincronizar despesas, orçamento e cofre com todos da família.",
      setActiveHint: "Toque no cartão para usar esta página inicial no aplicativo.",
      membersScreenTitle: "Membros da casa",
      membersSubtitle: "Pessoas com acesso a esta casa e suas funções.",
      memberRoleOwner: "Proprietário",
      memberRoleAdmin: "Administrador",
      memberRoleMember: "Membro",
      memberSince: "Desde {{date}}",
      invitePendingTitle: "Convites pendentes",
      inviteExpires: "Expira em {{date}}",
      inviteExpired: "Expirado",
      inviteEmpty: "Nenhum convite pendente.",
      membersEmpty: "Nenhum membro nesta casa.",
      inviteCta: "Convidar membro",
      inviteComingSoon: "Os convites são gerenciados pela web. ",
      createScreenTitle: "Nova casa",
      createSubtitle: "Dê um nome à sua casa para começar a monitorar as despesas.",
      nameLabel: "Nome da casa",
      namePlaceholder: "Por exemplo ",
      createCta: "Criar casa",
      creating: "Criando…",
      createSuccess: "Página inicial criada com sucesso.",
      createError: "Não foi possível criar a casa. ",
      financialProfileCta: "Perfil financeiro",
      financingsCta: "Financiamentos",
      financialProfileScreenTitle: "Perfil financeiro",
      financialProfileSubtitle: "Saldo de referência, moeda e limite de orçamento mensal.",
      financialProfileFinancingsCta: "Veja financiamentos residenciais",
      openingBalanceLabel: "Saldo inicial (conta)",
      openingBalancePlaceholder: "0,00",
      currencyLabel: "Moeda (ISO 4217)",
      currencyPlaceholder: "EUR",
      monthlyBudgetLabel: "Limite mensal (opcional)",
      monthlyBudgetPlaceholder: "0,00",
      saveProfileCta: "Salvar perfil",
      savingProfile: "Salvando…",
      saveProfileSuccess: "Perfil financeiro atualizado.",
      saveProfileError: "Não foi possível salvar. ",
      invalidAmount: "Insira um valor válido (0 ou superior).",
      inviteEmailSection: "Convidar por e-mail",
      inviteRoleLabel: "Papel",
      inviteEmailLabel: "E-mail do convidado",
      inviteEmailPlaceholder: "nome@exemplo.com",
      inviteSendCta: "Enviar convite",
      inviteSending: "Enviando…",
      inviteSentSuccess: "Convite enviado. ",
      inviteError: "Não foi possível enviar o convite. ",
      revokeInviteCta: "Revogar",
      revokeInviteConfirm: "Revogar este convite? ",
      revokeInviteSuccess: "Convite revogado.",
      revokeInviteError: "Não foi possível revogar. ",
      residentsTitle: "Residentes",
      manageAllCta: "Gerenciar tudo",
      splitAllocationTitle: "Alocação dividida",
      allocatedBudgetLabel: "Orçamento alocado",
      perMonthSuffix: "/ mês",
      adjustSplitCta: "Ajustar divisão",
      soloMemberHint: "Somente você nesta propriedade.",
      inviteCompactCta: "Convidar",
      inviteCardTitle: "Convide um membro",
      inviteCardBody: "Compartilhe despesas facilmente convidando outros residentes para sua casa.",
      inviteCardCta: "Vá para membros e convide",
      proTipTitle: "Dica de economia",
      proTipBody:
        "As famílias com divisões alinhadas ao uso real geralmente economizam mais em serviços públicos. ",
      proTipLink: "Ver orçamento familiar",
      settingsA11y: "Configurações iniciais",
      useAsActiveCta: "Use como casa ativa",
      setActiveSuccess: "Casa ativa atualizada.",
      removeMemberCta: "Remover",
      removeMemberConfirm: "Remover esta pessoa do lar?",
      removeMemberSuccess: "Membro removido do lar.",
      removeMemberError: "Não foi possível remover. Tente novamente.",
      changeRoleCta: "Alterar função",
      changeRoleTitle: "Nova função",
      roleUpdatedSuccess: "Função atualizada.",
      roleUpdateError: "Não foi possível alterar a função. Tente novamente.",
      inviteAdminOnly: "Apenas proprietários e administradores podem convidar.",
    },
    budget: {
      screenTitle: "Orçamento rateado",
      subtitle: "Gasto planejado x real para a casa ativa.",
      heroMainTitle: "Dinheiro necessário",
      heroMainAccent: "este mês",
      netBalanceLabel: "Saldo líquido estimado",
      netBalanceMock: "624,30€",
      progressLabel: "Do limite mensal utilizado",
      progressPercent: "{{percent}}%",
      rowCategory: "Categoria",
      rowBudgeted: "Planejado",
      rowActual: "Real",
      demoUtilitiesBudgeted: "120€",
      demoUtilitiesActual: "89€",
      demoInsuranceBudgeted: "85€",
      demoInsuranceActual: "85€",
      emptyRows: "Não existe nenhum plano de orçamento de categoria para este mês.",
      emptyPerformanceTitle: "Ainda não há dados de desempenho",
      emptyPerformanceDescription:
        "Defina valores planejados por categoria para comparar com o que você realmente gasta.",
      proratedTotalLabel: "Total rateado",
      moneyRequiredLabel: "Total necessário",
      remanentLabel: "Restante {{amount}}",
      proratedInstallmentType: "Parcela",
      proratedInstallmentMonthly: "Mensal",
      proratedItemTotal: "Total {{amount}}",
      proratedMoveHint: "Valor mensal reservado para pagamentos anuais.",
      adjustGoalsCta: "Ajustar metas",
      fabEditBudget: "Editar orçamento",
      fabCreateBudget: "Criar orçamento",
      performanceSectionTitle: "Orçado versus real",
      performanceSectionSubtitle: "Orçamento planejado comparado com os gastos reais.",
      performanceSectionPlanNote:
        "“Plano” é o valor que você economiza ao editar o orçamento mensal da categoria; ",
      filtersCta: "Filtros",
      exportPdfCta: "Exportar PDF",
      exportPdfComingSoon: "A exportação de PDF estará disponível em breve.",
      editByCategoryTitle: "Planejamento de categoria",
      editByCategorySubtitle: "Defina o valor planejado para cada categoria neste mês.",
      createCategoryCta: "Nova categoria",
      createCategoryTitle: "Criar categoria",
      createCategorySubtitle: "Adicione uma categoria para usar em despesas e orçamentos.",
      categoryCreatedSuccess: "Categoria criada com sucesso.",
      deleteCategoryA11y: "Excluir categoria {{name}}",
      deleteCategoryConfirmTitle: "Excluir categoria",
      deleteCategoryConfirmMessage:
        "Tem certeza de que deseja excluir “{{name}}”? ",
      deleteCategoryCta: "Excluir",
      deleteCategorySuccess: "Categoria excluída.",
      deleteCategoryError: "Não foi possível excluir a categoria. ",
    },
    analytics: {
      screenTitle: "Informações sobre a família",
      openFromBudget: "Insights e gráficos",
      backToBudget: "Orçamento",
      subtitle: "Últimos {{count}} meses — sinais pagos x planejados e de categoria.",
      empty: "Não há despesas pagas neste período. ",
      chartTitle: "Por mês",
      legendPaid: "Pago",
      legendPlanned: "Planejado",
      legendAvg: "Média paga",
      chartScrollHint:
        "Deslize o gráfico para o lado para ver todos os meses; ",
      chartYAxisA11y: "Eixo de quantidade vertical de zero a {{max}}.",
      peakTitle: "Mês de pico",
      peakBody: "{{month}} · {{amount}}",
      peakNone: "Sem dados",
      avgTitle: "Média paga/mês ativo",
      avgHint: "Excluem-se os meses com zero gastos pagos.",
      topCategories:
        "Maior gasto acumulado por categoria (últimos {{count}} meses)",
      otherCategories: "Outras categorias: {{amount}}",
      overspendTitle: "Maior ultrapassagem acumulada (últimos {{count}} meses)",
      overspendBody:
        "“{{name}}”: {{amount}} acima do plano em meses com linha, acumulado nos últimos {{count}} meses.",
      reviewTitle: "Revisão (muitas vezes acima do planejado)",
      reviewHint: "≥80% dos meses orçados ultrapassaram o planejado (com histórico suficiente).",
      reviewEmpty: "Nenhum para esta janela.",
      reduceTitle: "Plano de corte (geralmente abaixo)",
      reduceHint: "≥80% dos meses orçados estavam dentro do planejado.",
      reduceEmpty: "Nenhum para esta janela.",
    },
    vault: {
      screenTitle: "Cofre de garantia",
      subtitle: "Garantias e ativos com prazos de validade.",
      searchPlaceholder: "Procure no cofre…",
      alertTitle: "Próxima expiração",
      alertBody: "A garantia do refrigerador expira em 45 dias.",
      alertBodyLive: "A garantia para “{{name}}” expirará em breve. ",
      itemFridge: "Geladeira · cozinha",
      itemInsurance: "Seguro residencial",
      completionLabel: "Inventário concluído",
      empty: "Nenhum item no cofre desta casa.",
      emptyTitle: "Seu cofre está vazio",
      emptyDescription:
        "Cadastre eletrodomésticos, móveis e apólices com datas de garantia. ",
      emptyFilterTitle: "Nada nesta categoria",
      emptyFilterDescription:
        "Tente outra guia ou adicione um recurso que se enquadre nesta seção.",
      detailScreenTitle: "Cofre · Ativo",
      detailBadge: "Registro",
      detailTitle: "Detalhes do recurso",
      detailSubtitle: "Informações de compra originais e garantia.",
      detailAssetTypeLabel: "Categoria",
      detailPurchaseDateLabel: "Data de compra",
      detailWarrantyEndsLabel: "A garantia termina",
      detailNotesLabel: "Notas",
      detailDocumentLabel: "Recibo",
      detailDocumentPresent: "Documento anexado",
      detailDocumentMissing: "Sem recibo",
      detailNoAssetType: "Nenhuma categoria",
      detailNoPurchaseDate: "Não especificado",
      detailNoWarrantyEnds: "Sem vencimento",
      detailNoNotes: "Sem notas",
      detailEditCta: "Editar",
      detailWarrantyExpired: "Garantia expirou",
      detailWarrantyActiveDays: "{{days}} dias restantes",
      createScreenTitle: "Adicionar ao cofre",
      editScreenTitle: "Editar recurso",
      saveSuccess: "Ativo salvo no cofre.",
      editSuccess: "Ativo atualizado com sucesso.",
      saveError: "Não foi possível salvar o ativo. ",
      deleteCta: "Excluir recurso",
      deleteConfirm: "Excluir este ativo do cofre? ",
      deleteSuccess: "Ativo excluído do cofre.",
      deleteError: "Não foi possível excluir. ",
      nameLabel: "Nome do item",
      namePlaceholder: "Por exemplo ",
      assetTypeLabel: "Categoria",
      assetTypePlaceholder: "Por exemplo ",
      purchaseDateLabel: "Data de compra",
      warrantyEndsLabel: "Expiração da garantia",
      notesLabel: "Notas",
      notesPlaceholder: "Detalhes do contrato, referências…",
      saveCta: "Salvar no cofre",
      saving: "Salvando…",
      heroEyebrow: "Armazenamento de documentos",
      heroTitle: "Cofre",
      heroDescription:
        "Gerencie com segurança as garantias e apólices de seguro de sua casa. ",
      criticalEyebrow: "Alerta crítico",
      criticalBodyUrgent:
        "A garantia para “{{name}}” expira em {{days}} dias. ",
      criticalBodyExpired: "A garantia para “{{name}}” expirou. ",
      criticalCtaDetails: "Ver detalhes",
      criticalCtaExtend: "Estender plano",
      statTotalLabel: "Ativos totais",
      statDocsLabel: "Com recibo",
      filterAll: "Todos os itens",
      sectionCount: "{{count}} itens",
      cardPurchaseLabel: "Data de compra",
      cardExpiresLabel: "Data de validade",
      viewReceiptCta: "Ver recibo",
      registerNewCta: "Registrar novo item",
      badgeExpired: "Expirado",
      badgeEndsToday: "Termina hoje",
      badgeDaysLeft: "Faltam {{count}} dias",
      badgeMonthsLeft: "Faltam {{count}} meses",
      badgeYearsLeft: "{{count}} anos restantes",
      badgeSafe: "Seguro",
      badgeNoDate: "Sem data",
      formEyebrow: "Cadastro",
      formTitle: "Detalhes do novo ativo",
      formSubtitle: "Proteja seu investimento fornecendo as informações originais da compra.",
      uploadSectionLabel: "Comprovante de boleto/fatura",
      uploadTitle: "Carregar comprovante de compra",
      uploadSubtitle: "Escolha um arquivo ou foto da sua galeria",
      uploadTypesHint: "JPG, PNG, WebP, GIF ou PDF · até {{maxMb}} MB",
      uploadPickFilesCta: "Escolha o arquivo",
      uploadPickGalleryCta: "Galeria",
      uploadUploading: "Fazendo upload…",
      uploadAttachedHint: "Anexado: {{fileName}}",
      uploadRemoveCta: "Remover",
      uploadRemoveA11y: "Remover documento anexado",
      uploadErrorMime: "Tipo de arquivo não permitido. ",
      uploadErrorSize: "O arquivo excede o tamanho máximo ({{maxMb}} MB).",
      uploadPermissionDenied:
        "Acesso à biblioteca de fotos negado. ",
      uploadErrorGeneric: "Não foi possível fazer upload do arquivo. ",
      uploadSuccessReady: "Arquivo pronto. ",
      uploadSoon: "O upload do arquivo estará disponível em breve.",
      completionStatusLabel: "Status de conclusão do cofre",
      saveDisclaimer:
        "Ao salvar, você autoriza a criptografia segura na nuvem de seus documentos de compra e datas de garantia.",
      moreOptionsA11y: "Mais opções",
    },
    journalFilters: {
      screenTitle: "Filtros",
      badge: "Refinar visualização",
      title: "Filtros",
      subtitle: "Ajuste os critérios para encontrar despesas específicas.",
      categoriesSection: "Categorias",
      membersSection: "Membros",
      dateRangeSection: "Período",
      paidStatusSection: "Status do pagamento",
      scopeSection: "Âmbito",
      fromLabel: "De",
      toLabel: "Para",
      paidOnly: "Pago apenas",
      unpaidOnly: "Somente não pago",
      allStatuses: "Todos",
      applyCta: "Aplicar filtros",
      resetCta: "Reiniciar",
      applying: "Aplicando…",
    },
    profile: {
      screenTitle: "Conta",
      settingsItem: "Configurações de Conta",
      settingsItemSub: "Gerencie seus dados pessoais",
      notificationsItem: "Notificações",
      notificationsItemSub: "Alertas e atualizações de atividades",
      securityItem: "Privacidade e segurança",
      securityItemSub: "Senha e permissões",
      helpItem: "Ajuda e suporte",
      helpItemSub: "Perguntas frequentes e central de contato",
      signOutItem: "sair",
      signOutItemSub: "Sair com segurança",
      deleteAccountItem: "Eliminar conta",
      deleteAccountItemSub: "Remover permanentemente os seus dados e acesso",
      deleteAccountConfirmTitle: "Eliminar a conta?",
      deleteAccountConfirmMessage:
        "O seu utilizador e dados pessoais serão eliminados permanentemente. Esta ação não pode ser anulada.",
      deleteAccountConfirmCta: "Eliminar conta",
      deleteAccountError: "Não foi possível eliminar a conta. Tente novamente.",
      totalAssetsLabel: "Ativos totais",
      activeBudgetsLabel: "Meses com orçamento",
      comingSoon: "Em breve.",
    },
    settings: {
      hubTitle: "Conta",
      hubSubtitle: "Gerencie seu perfil, segurança e preferências de aplicativos.",
      backToAccount: "Voltar para a conta",
      cardPersonalTitle: "Dados pessoais",
      cardPersonalHint: "Nome, e-mail e idioma da interface.",
      cardPasswordTitle: "Senha",
      cardPasswordHint: "Atualize sua senha de login.",
      cardSessionsTitle: "Dispositivos e sessões",
      cardSessionsHint: "Sessões ativas em outros navegadores ou dispositivos.",
      cardPasskeysTitle: "Passkeys e biometria",
      cardPasskeysHint: "Registro passkey pre-auth e acesso biométrico local.",
      cardNotificationsTitle: "Notificações",
      cardNotificationsHint: "Push, alertas e dispositivos registrados.",
      cardSupportTitle: "Ajuda e suporte",
      cardSupportHint: "Recuperar acesso e recursos de ajuda.",
      cardAppearanceTitle: "Aparência",
      cardAppearanceHint: "Claro, escuro ou compatível com as configurações do sistema.",
      avatarChangeA11y: "Alterar foto do perfil",
      avatarUploading: "Fazendo upload da foto…",
      clearAvatar: "Remover foto do perfil",
      avatarPickError: "Escolha uma imagem (JPG, PNG, WebP ou GIF).",
      avatarUploadError: "Não foi possível atualizar sua foto. ",
      footerBrand: "HomeExpense",
      personalTitle: "Dados pessoais",
      personalSubtitle: "Seu e-mail não pode ser alterado no aplicativo.",
      emailLabel: "E-mail",
      emailHint: "Para alterar seu e-mail, entre em contato com o suporte.",
      nameField: "Nome de exibição",
      profileFormSectionTitle: "Perfil e idioma",
      saveButton: "Salvar alterações",
      saving: "Salvando…",
      savedHint: "Alterações salvas.",
      appearanceTitle: "Aparência",
      appearanceSubtitle: "Escolha a aparência do aplicativo no seu dispositivo.",
      appearanceLight: "Luz",
      appearanceDark: "Escuro",
      appearanceSystem: "Sistema",
      appearanceCurrent: "Selecionado",
      sessionsTitle: "Dispositivos e sessões",
      sessionsSubtitle: "Sessões com acesso à sua conta.",
      sessionsIntro:
        "Você pode desconectar outros dispositivos. ",
      sessionsLoadError: "Não foi possível carregar as sessões.",
      sessionsEmpty: "Nenhuma outra sessão encontrada.",
      sessionsSignOutOthers: "Sair de outras sessões",
      sessionsSignOutDevice: "sair",
      sessionsThisDevice: "Este dispositivo",
      sessionsCurrentBadge: "Atual",
      sessionsLastActive: "Último ativo",
      sessionsStarted: "Conectado",
      sessionsExpires: "Expira",
      sessionsIp: "PI",
      sessionsUnknownUa: "Cliente desconhecido",
      sessionsSignOutDeviceConfirm: "Sair deste dispositivo?",
      sessionsSignOutOthersConfirm: "Sair de todas as sessões, exceto esta?",
      sessionsActionError: "Não foi possível concluir a ação. ",
      passkeysTitle: "Passkeys e biometria",
      passkeysSubtitle:
        "Gerencie o desbloqueio rápido neste dispositivo e prepare o contexto pre-auth para registrar passkeys.",
      passkeysBiometricTitle: "Verificar desbloqueio biométrico",
      passkeysBiometricHint: "Teste Face ID / Touch ID / digital com sua sessão atual.",
      passkeysBiometricSuccess: "Desbloqueio biométrico validado neste dispositivo.",
      passkeysBiometricError: "Não foi possível validar a biometria. ",
      passkeysContextTitle: "Gerar contexto passkey pre-auth",
      passkeysContextHint:
        "Gere um contexto assinado temporário para continuar o registro de passkey em clientes compatíveis.",
      passkeysContextSuccess: "Contexto passkey pre-auth gerado.",
      passkeysContextSuccessHint: "Expira em {{seconds}} segundos.",
      passkeysContextError: "Não foi possível gerar o contexto passkey. ",
      notificationsTitle: "Notificações",
      notificationsSubtitle: "Preferências de alerta e registro push neste dispositivo.",
      pushSectionTitle: "Notificações push",
      activatePush: "Habilitar ou atualizar push",
      pushWorking: "Notificações push estão disponíveis neste dispositivo.",
      pushConfigMissing: "O Firebase não está configurado no aplicativo.",
      pushPermissionDenied: "Permissão de notificação negada. ",
      pushRegisterSuccess: "Dispositivo registrado para notificações push.",
      pushFailureNoSession: "Faça login para registrar este dispositivo para receber notificações.",
      pushFailurePermissionRequest: "Não foi possível concluir a solicitação de permissão de notificação.",
      pushFailureIosRemote:
        "Não foi possível ativar notificações remotas neste iPhone. ",
      pushFailureToken:
        "Não foi possível obter o token de notificação. ",
      pushRegisterError: "Não foi possível registrar o dispositivo para notificações.",
      prefsSectionTitle: "Preferências",
      prefPushMaster: "Habilitar notificações push",
      prefHouseInvites: "Casa e convites",
      prefExpenses: "Despesas e pagamentos",
      prefBudget: "Orçamento",
      prefReminders: "Lembretes",
      prefScanDocs: "Digitalização e documentos",
      prefDigests: "Resumos",
      prefUpcomingCharges: "Cobranças com vencimento amanhã (não pagas)",
      devicesSectionTitle: "Dispositivos registrados",
      noPushDevices: "Nenhum dispositivo registrado para push.",
      lastSeenLabel: "Visto pela última vez",
      devicePushLabel: "Push ativado",
      removeDevice: "Remover",
      removeDeviceConfirm: "Remover este dispositivo da lista?",
      platformWeb: "Rede",
      platformIos: "iOS",
      platformAndroid: "Android",
      supportTitle: "Ajuda e suporte",
      supportSubtitle: "Recursos para recuperar acesso ou obter ajuda.",
      supportIntro:
        "Se não conseguir acessar sua conta, você pode solicitar um link de redefinição de senha.",
      supportForgotTitle: "Esqueceu sua senha?",
      supportForgotBody: "Enviaremos um link seguro para o e-mail que você fornecer.",
      supportForgotCta: "Solicitar link de recuperação",
    },
    scan: {
      screenTitle: "Varredura de IA",
      subtitle: "Capture um recibo para extrair o valor automaticamente.",
      scanningHint: "Enquadre o documento na área de digitalização.",
      openGallery: "Galeria",
      manualEntry: "Entrada manual",
      recentTitle: "Verificações recentes",
      empty: "Nenhum recibo digitalizado para esta casa ainda.",
      emptyTitle: "Nenhuma verificação recente",
      emptyDescription:
        "Faça upload de uma foto de um recibo e extrairemos o valor automaticamente com IA.",
      emptyCta: "Carregar recibo",
      statusPending: "Pendente",
      statusProcessing: "Processamento",
      statusCompleted: "Concluído",
      statusFailed: "Fracassado",
      uploading: "Fazendo upload da imagem…",
      registering: "Registrando digitalização…",
      uploadSuccess: "Recibo recebido. ",
      pollHint: "Analisando recebimento com IA…",
      createExpenseFromScan: "Crie despesas com esses dados",
      extractedTitle: "Dados detectados",
      extractedMerchant: "Comerciante",
      extractedDate: "Data",
      extractedTotal: "Total",
      extractionErrorFallback: "Não foi possível interpretar o recibo.",
      permissionDenied: "Permissão da câmera negada. ",
      permissionBlocked: "Permissão bloqueada. ",
    },
    addExpense: {
      createTitle: "Nova despesa",
      editTitle: "Editar despesa",
      saveSuccess: "Despesa salva com sucesso.",
      editSuccess: "Despesa atualizada com sucesso.",
      saveError: "Não foi possível salvar a despesa. ",
      invalidAmount: "Insira um valor válido.",
      invalidCurrency: "Insira um código de moeda ISO válido (3 letras, por exemplo, EUR).",
      titleOrCategoryRequired: "Adicione um título ou selecione uma categoria.",
      titleLabel: "Título",
      titlePlaceholder: "Por exemplo ",
      amountLabel: "Quantia",
      dateLabel: "Data",
      categoryLabel: "Categoria",
      categoryNone: "Nenhuma categoria",
      paymentMethodLabel: "Método de pagamento",
      paymentMethodNone: "Não especificado",
      paymentMethodCard: "Cartão",
      paymentMethodCash: "Dinheiro",
      paymentMethodAccount: "Conta",
      paidByLabel: "Pago por",
      paidByDefault: "Conta doméstica",
      paidNowLabel: "Marcar como pago agora",
      notesLabel: "Notas",
      notesPlaceholder: "Detalhes ou referências…",
      receiptLabel: "Comprovativo",
      receiptAttachCta: "Anexar PDF ou imagem",
      receiptAttachedHint: "Ficheiro pronto para guardar com a despesa.",
      receiptClearA11y: "Remover comprovativo",
      receiptUploading: "A enviar…",
      receiptUploadError: "Não foi possível enviar. Tente novamente.",
      receiptLinkedExisting: "Já existe um comprovativo; pode substituir ou remover.",
      isRecurringLabel: "Despesa recorrente",
      recurrenceKindLabel: "Tipo de recorrência",
      recurrenceMonthlyFixed: "Fixo mensalmente",
      recurrenceSeries: "Série personalizada",
      frequencyLabel: "Freqüência",
      freqMonthly: "Mensal",
      freqBimonthly: "Bimestralmente",
      freqQuarterly: "Trimestral",
      freqSemiannual: "Semestral",
      freqAnnual: "Anual",
      endsOnLabel: "Data de término",
      endsOnPlaceholder: "AAAA-MM-DD (opcional)",
      proratedLabel: "Valor proporcional",
      monthlyFixedHint: "{{count}} parcelas mensais serão criadas automaticamente.",
      submitCreate: "Criar despesa",
      submitCreateRecurring: "Crie séries recorrentes",
      submitSave: "Salvar alterações",
      beforeSubmitSummaryTitle: "Resumo antes de salvar",
      summaryConcept: "Título",
      summaryUnnamed: "(sem título)",
      summaryTotalAmount: "Montante total",
      summaryInstallmentAmount: "Parcela para este período (parcelado)",
      summaryDate: "Data",
      summaryCategory: "Categoria",
      summaryPaymentMethod: "Método de pagamento",
      summaryPaidBy: "Pago por",
      summarySharedSplit: "Dividir (esta parcela)",
      summaryScheduleTitle: "Cobranças do diário (visualização)",
      summaryMoreDates: "… e {{count}} mais",
      enterAmountForSplit: "Insira um valor válido para ver a divisão.",
      sharedPreviewRangeInvalid: "Confira as datas de início e término da série.",
      sharedPreviewFinalizeRecurrence: "Complete a recorrência (valor e datas) para ver o parcelamento.",
      flows: {
        launcherTitle: "O que pretende fazer?",
        launcherSubtitle:
          "Escolha um percurso conforme o tipo de despesa que pretende registar.",
        manualOnlyBanner:
          "Introduza os dados principais da despesa: valor, data e método de pagamento.",
        intentQuickTitle: "Registar uma despesa pontual",
        intentQuickDescription:
          "Para um pagamento que já fez: montante, categoria e como pagou. O resto só se precisar.",
        intentRecurringTitle: "Despesa fixa todos os meses",
        intentRecurringDescription:
          "Uma prestação que se repete (renda, condomínio…). Um assistente passo a passo explica o que está a configurar.",
        intentSeriesTitle: "Série com datas ou rateio",
        intentSeriesDescription:
          "Prestações com data de fim, outras frequências ou repartição de um total por meses. Resumo antes de guardar.",
        quickExpandMore: "Mais opções (título, notas, partilhado…)",
        repeatLastFlowCta: "Continuar com: {{flowLabel}}",
        navNext: "Seguinte",
        navBack: "Voltar",
        navReview: "Ver resumo",
        editStepA11y: "Editar passo {{step}}",
        monthlyWizard: {
          step1Title: "Que despesa é?",
          step1Help: "Título ou categoria para a reconhecer todos os meses no diário.",
          step2Title: "Quanto paga por mês?",
          step2Help: "Um único montante mensal. Repetimos em cada prestação.",
          step3Title: "Quando começa?",
          step3Help: "Data do primeiro débito ou do dia em que deve aparecer no diário.",
          step4Title: "Como paga?",
          step4Help: "Cartão, numerário ou conta: indica como sai o dinheiro (tudo manual).",
          step5Title: "É partilhada em casa?",
          step5Help: "Quem paga e se a despesa é partilhada entre membros.",
          reviewTitle: "Assim ficará a sua despesa fixa",
          reviewHelp: "Revise os dados. Pode voltar atrás e alterar qualquer passo.",
        },
        seriesWizard: {
          step1Title: "Título e montante",
          step1Help:
            "O que é esta despesa e o montante conforme o modo (por mês ou total do período, conforme escolher a seguir).",
          step2Title: "Com que frequência se repete",
          step2Help: "Mensal, trimestral, anual… A frequência define as linhas no diário.",
          step3Title: "Início e fim",
          step3Help:
            "Data de início e, se aplicável, última prestação. Sem data de fim, a série pode ficar aberta conforme as regras do formulário.",
          step4Title: "Rateio (prorata)",
          step4Help:
            "Se reparte um total por várias prestações, aqui se explica a divisão. Sem banco: confirme os montantes.",
          step5Title: "Reparto entre pessoas",
          step5Help: "Partilhado e quem pagou. Verá uma pré-visualização quando montante e datas estiverem prontos.",
          reviewTitle: "Resumo da série",
          reviewHelp: "Confirme prestações e datas antes de criar a série.",
        },
      },
    },
    notifications: {
      screenTitle: "Notificações",
      empty: "Sem notificações",
      emptyHint: "Atualizações e alertas iniciais aparecerão aqui.",
      markAllRead: "Marcar tudo como lido",
      markAllReadConfirm: "Marcar todas as notificações como lidas?",
      markAllReadSuccess: "Todas as notificações marcadas como lidas.",
      markReadError: "Não foi possível marcar como lido.",
    },
    pendingInvitations: {
      screenTitle: "Convites pendentes",
      subtitle: "Casas para as quais você foi convidado. ",
      empty: "Nenhum convite pendente",
      emptyHint: "Quando alguém convida você para uma casa, ela aparecerá aqui.",
      inviterLine: "Convidado por: {{inviterName}}",
      expiresLabel: "Expira em {{date}}",
      acceptCta: "Aceite e participe",
      accepting: "Juntando-se…",
      acceptSuccess: "Você ingressou na casa com sucesso.",
      acceptError: "Não foi possível aceitar o convite. ",
    },
    financings: {
      screenTitle: "Financiamentos",
      empty: "Sem financiamentos",
      emptyHint: "Acompanhe empréstimos e parcelas fixas para monitorar o progresso.",
      detailScreenTitle: "Detalhe do financiamento",
      installmentLabel: "Parcela: {{amount}}",
      termProgressLabel: "{{percent}}% do prazo",
      contractStartLabel: "Início do contrato",
      endsOnLabel: "Última parcela",
      remainingLabel: "Restante estimado: {{amount}}",
      openSeriesCta: "Ver despesas recorrentes",
      noSeries: "Sem despesas recorrentes vinculadas",
      aiSummaryTitle: "Resumo de IA",
      deleteCta: "Excluir financiamento",
      deleteConfirm: "Excluir este financiamento? ",
      deleteSuccess: "Financiamento eliminado.",
      deleteError: "Não foi possível excluir. ",
      remainingEstimatedShort: "Restante estimado",
      addDocumentCta: "Adicionar documento",
      removeDocumentA11y: "Remover documento",
      docMaxReached: "Máximo de {{max}} documentos.",
      docUploadError: "Não foi possível fazer upload do arquivo. ",
      docTooLarge: "O arquivo é muito grande.",
      docMimeNotAllowed: "Tipo de arquivo não permitido.",
    },
    demo: {
      title: "Notificações",
      itemLabel: "Item {{index}}",
      listSubtitle: "GET /notificações (cursor). ",
      loadingMore: "Carregando mais…",
      sheetTitle: "Exemplo de folha inferior",
      sheetBody:
        "Este modal usa @gorhom/bottom-sheet. ",
      sheetCloseHint: "Deslize para baixo ou toque fora para dispensar.",
      fetchError: "Não foi possível carregar a lista.",
      needSession: "Nenhuma sessão. ",
      emptyNotifications: "Nenhuma notificação nesta página.",
      notificationType: "Digite: {{type}}",
    },
    auth: {
      screenTitle: "Entrar",
      signInTitle: "bem vindo de volta",
      signInSubtitle: "Insira suas credenciais para acessar seu livro-razão.",
      registerTitle: "Criar uma conta",
      registerSubtitle: "Comece hoje com clareza financeira em casa.",
      registerScreenTitle: "Cadastre-se",
      forgotScreenTitle: "Recuperar acesso",
      forgotBadge: "Portal de segurança",
      forgotTitle: "Recupere seu acesso financeiro.",
      forgotTitleLead: "Recupere seu",
      forgotTitleAccent: "acesso financeiro.",
      forgotSubtitle: "Acesso",
      forgotBody:
        "Digite o endereço de e-mail da sua conta HomeExpense. ",
      forgotEmailLabel: "Endereço de e-mail registrado",
      forgotPlaceholderEmail: "por exemplo ",
      contactSupport: "Contate o suporte",
      contactSupportHint: "O suporte no aplicativo estará disponível em breve. ",
      forgotDecorLetters: "LE",
      forgotDecorBrand: "Livro razão imobiliário",
      forgotDecorVersion: "v.4.0.2",
      subtitle: "Use a mesma conta da web (e-mail e senha).",
      nameLabel: "Nome completo",
      namePlaceholder: "Seu nome",
      emailLabel: "Endereço de email",
      emailPlaceholder: "nome@exemplo.com",
      passwordLabel: "Senha",
      passwordPlaceholder: "••••••••",
      confirmPasswordLabel: "Confirmar",
      signInCta: "Faça login no Ledger",
      registerCta: "Crie uma conta gratuita",
      sendRecoveryCta: "Enviar link de recuperação",
      signingIn: "Fazendo login…",
      registering: "Criando conta…",
      sendingRecovery: "Enviando…",
      signOut: "sair",
      goToSignIn: "Entrar",
      goToRegister: "Criar uma conta",
      goToForgot: "Esqueceu sua senha?",
      backToSignIn: "Voltar para fazer login",
      orContinueWith: "Ou continue com",
      orRegisterWithEmail: "Ou cadastre-se com e-mail",
      socialGoogle: "Google",
      socialApple: "Maçã",
      signInWithApple: "Faça login com a Apple",
      signUpWithApple: "Inscreva-se na Apple",
      signInWithPasskey: "Entrar com Passkey",
      signUpWithPasskey: "Cadastrar com Passkey",
      continuingWithGoogle: "Conectando-se ao Google…",
      continuingWithApple: "Conectando-se com a Apple…",
      continuingWithPasskey: "Conectando-se com Passkey…",
      socialApplePending: "O login da Apple no celular estará disponível em breve.",
      socialPasskey: "Passkey",
      errorGoogleFailed: "Não foi possível fazer login com o Google. ",
      errorGoogleCancelled: "O login do Google foi cancelado.",
      errorGoogleConfigMissing:
        "O Login do Google não está configurado neste aplicativo. ",
      errorAppleFailed: "Não foi possível fazer login com a Apple. ",
      errorAppleCancelled: "O login com a Apple foi cancelado.",
      errorAppleConfigMissing:
        "Apple Sign-In não está configurado neste app. Verifique APPLE_CLIENT_ID e APPLE_APP_BUNDLE_IDENTIFIER.",
      errorPasskeyFailed: "Não foi possível entrar com Passkey. ",
      errorPasskeyUnavailable: "Seu dispositivo não oferece suporte a Passkey neste fluxo.",
      biometricPromptTitle: "Desbloquear HomeExpense",
      socialUnavailable: "O login social no celular estará disponível em breve.",
      termsAgree: "Eu concordo com o",
      termsLink: "Termos de Serviço",
      privacyLink: "política de Privacidade",
      termsConnector: "e",
      termsRequired: "Você deve aceitar os termos e a política de privacidade para continuar.",
      alreadyHaveAccount: "Já tem uma conta?",
      newHere: "Novo aqui?",
      sessionActive: "Conectado",
      forgotSentHint:
        "Se existir uma conta para esse e-mail, você receberá um link em breve.",
      signIn: "Entrar",
      errorInvalidCredentials: "E-mail ou senha incorretos.",
      errorGeneric: "Não foi possível fazer login. Tente novamente.",
      errorPasswordMismatch: "As senhas não coincidem.",
      errorSignUpFailed: "Não foi possível criar a conta. ",
      errorRecoveryFailed: "Não foi possível enviar o link. ",
      registerCheckEmail:
        "Verifique seu e-mail para verificar sua conta antes de fazer login.",
      changePasswordScreenTitle: "Alterar a senha",
      changePasswordBadge: "CONFIGURAÇÕES DE SEGURANÇA",
      changePasswordTitle: "Atualize suas credenciais",
      changePasswordSubtitle:
        "Mantenha seus dados financeiros seguros com uma senha forte e exclusiva.",
      currentPasswordLabel: "Senha atual",
      currentPasswordPlaceholder: "••••••••••••",
      newPasswordLabel: "Nova Senha",
      newPasswordPlaceholder: "••••••••••••",
      confirmNewPasswordLabel: "Confirme a nova senha",
      changePasswordCta: "Atualizar senha",
      changingPassword: "Atualizando…",
      changePasswordSuccess: "Senha atualizada",
      changePasswordSuccessHint:
        "Sua senha foi alterada. ",
      errorChangePasswordFailed:
        "Não foi possível atualizar a senha. ",
      errorChangePasswordWrong:
        "A senha atual está incorreta. ",
      passwordStrengthWeak: "Fraco",
      passwordStrengthFair: "Justo",
      passwordStrengthGood: "Bom",
      passwordStrengthStrong: "Forte",
      passwordStrengthLabel: "Nível de segurança",
      securityTipsTitle: "Dicas de segurança",
      securityTip1Title: "Singularidade",
      securityTip1Body:
        "Não reutilize senhas de outros serviços financeiros.",
      securityTip2Title: "Complexidade",
      securityTip2Body:
        "Misture letras maiúsculas, números e símbolos especiais ($@!).",
      changePasswordSignOutHint:
        "Alterar sua senha desconectará você de todos os outros dispositivos.",
    },
  },
  marketing: marketingPt,
  api: {
    errors: {
      validationFailed: "Os dados enviados são inválidos",
      unauthorized: "Não autenticado",
      forbidden: "Você não tem permissão para esta ação",
      internal: "Erro do Servidor Interno",
      fallback: "Pedido ruim",
      validation: {
        expectedString: "O campo {{property}} deve ser uma string",
        invalidUuid: "O campo {{property}} deve ser um UUID válido",
        expectedInt: "O campo {{property}} deve ser um número inteiro",
        expectedBoolean: "O campo {{property}} deve ser booleano",
        expectedArray: "O campo {{property}} deve ser uma matriz",
        expectedObject: "O campo {{property}} deve ser um objeto",
        invalidEnum: "O campo {{property}} tem um valor inválido",
        min: "O campo {{property}} está abaixo do mínimo permitido",
        max: "O campo {{property}} excede o máximo permitido",
        pattern: "O campo {{property}} tem um formato inválido",
        invalidField: "O campo {{property}} é inválido",
      },
      houses: {
        nameRequired: "O nome é obrigatório",
        createFailed: "Não foi possível criar a família",
        notFound: "Família não encontrada",
        nameEmpty: "O nome não pode ficar vazio",
        userIdRequired: "ID do usuário é obrigatório",
        cannotAssignOwnerViaApi: "A função de proprietário não pode ser atribuída por meio deste endpoint",
        userAlreadyMember: "O usuário já é membro",
        memberNotFound: "Membro não encontrado",
        atLeastOneOwner: "Deve haver pelo menos um proprietário",
        defaultSplitBpsRange: "defaultSplitBps deve estar entre 0 e 10.000",
        cannotRemoveSoleOwner: "Não é possível remover o único proprietário",
        financialProfileNotFound: "Perfil financeiro não encontrado",
        currencyIso: "A moeda deve ser ISO 4217 (3 letras)",
        invitationEmailInvalid: "E-mail de convite inválido",
        invitationNotFound: "Convite não encontrado ou não é mais válido",
        invitationTokenInvalid: "Link de convite inválido",
        invitationEmailMismatch: "Faça login com o endereço de e-mail para o qual o convite foi enviado",
        invitationEmailFailed: "Não foi possível enviar o convite. ",
        invitationUserMissing: "Conta de usuário não encontrada",
      },
      access: {
        notMember: "Você não é membro desta família",
        adminRequired: "Função de administrador ou proprietário necessária",
        ownerOnly: "Somente o proprietário pode realizar esta ação",
      },
      expenses: {
        userNotMember: "O usuário {{userId}} não é membro desta família",
        notFound: "Despesa não encontrada",
        titleRequired: "O título é obrigatório",
        amountMinorInvalid: "montanteMinor é inválido",
        categoryInvalidForHouse: "Categoria inválida para esta família",
        categoryInvalid: "Categoria inválida",
        currencyIso: "A moeda deve ser ISO 4217",
        currencyInvalid: "Moeda inválida",
        createFailed: "Não foi possível criar a despesa",
        titleEmpty: "O título não pode ficar vazio",
        splitSumMustBe10000: "A soma dos shareBps deve ser exatamente 10.000",
        splitDuplicateUser:
          "As divisões não podem incluir o mesmo usuário mais de uma vez (userId duplicado nas divisões)",
        splitShareBpsInvalid: "ShareBps inválido (deve estar entre 0 e 10.000)",
        recurrenceEndsBeforeStart: "A data de término deve ser igual ou posterior à data de início",
        recurrenceNoOccurrences: "Nenhuma data de parcelamento está dentro do intervalo selecionado",
        proratedRequiresEndsOn:
          "Com frequência mensal, o rateio do total requer uma data de término da série (ou use bimestral/trimestralmente... para dividir o total de cada período em linhas mensais sem data de término)",
        seriesNotFound: "Série recorrente não encontrada",
        seriesFixedSplitsRequired:
          "Fornece ações divididas para uma série de divisão fixa",
        seriesSplitsOnlyForFixed:
          "As divisões só podem ser enviadas quando a série usa o modo de divisão fixa",
        seriesEndsBeforeAnchor:
          "A data de término não pode ser anterior à data de início da série",
        seriesEndedWithFutureEndsOn:
          "Para encurtar a série no futuro, altere apenas a data de término sem marcar a série como encerrada.",
      },
      financings: {
        notFound: "Financiamento não encontrado",
        titleRequired: "O título é obrigatório",
        installmentInvalid: "A parcela mensal é inválida",
        contractStartInvalid: "A data de início do contrato é inválida",
        endsOnInvalid: "A data de término é inválida",
        endsBeforeContract: "A data de término não pode ser anterior ao início do contrato",
        noFutureInstallments:
          "Não há parcelas futuras a partir de hoje com essas datas. ",
        aiRequiresDocuments: "A análise de IA requer pelo menos um documento",
        documentStorageKeyRequired: "Cada documento deve ter uma chave de armazenamento",
        summarizationFailed: "Não foi possível gerar o resumo do contrato com IA",
        createFailed: "Não foi possível salvar o financiamento",
      },
      budgets: {
        yearMonthFormat: "anoMês deve ser AAAA-MM",
        plannedAmountInvalid: "plannerAmountMinor é inválido",
        categoryNotInHouse: "A categoria {{categoryId}} não pertence a esta família",
        categoryNotFound: "Categoria não encontrada",
      },
      vault: {
        notFound: "Item não encontrado",
        nameRequired: "O nome é obrigatório",
        nameEmpty: "O nome não pode ficar vazio",
        assetTypeInvalid:
          "Categoria inválida. ",
      },
      receiptScans: {
        notFound: "Verificação de recibo não encontrada",
        imageKeyRequired: "imageStorageKey é obrigatório",
        statusInvalid: "Status inválido",
        dailyLimitExceeded:
          "Você atingiu o limite diário de verificação de IA ({{limit}}). ",
        extractionFailed: "Não foi possível ler o recibo. ",
        extractionDisabled:
          "A extração de IA não está configurada no servidor. ",
        imageTooLarge: "A imagem é muito grande para ser processada com IA.",
        unsupportedMedia: "Formato de arquivo não compatível com esta verificação.",
      },
      categories: {
        nameRequired: "O nome é obrigatório",
        notFound: "Categoria não encontrada",
        nameEmpty: "O nome não pode ficar vazio",
      },
      dashboard: {
        yearMonthInvalid: "anoMês é inválido",
        yearMonthFormat: "anoMês deve ser AAAA-MM",
      },
      notifications: {
        notFound: "Notificação não encontrada",
        deviceNotFound: "Dispositivo não encontrado",
      },
      storage: {
        fileRequired: "É necessário um arquivo",
        mimeNotAllowed: "Tipo de arquivo não permitido: {{mime}}",
        fileTooLarge: "Arquivo muito grande. ",
        keyRequired: "A chave do objeto de armazenamento é obrigatória",
        invalidKey: "Chave de objeto de armazenamento inválida ou não permitida",
      },
      account: {
        userNotFound: "Utilizador não encontrado",
        deletionBlockedReferences:
          "Não foi possível concluir a eliminação da conta devido a referências de dados pendentes. Contacte o suporte.",
      },
      publicContact: {
        sendFailed: "Não foi possível enviar a mensagem. Tente novamente mais tarde.",
        serviceUnavailable:
          "O envio de mensagens não está disponível de momento (serviço de correio não configurado ou indisponível).",
      },
    },
  },
};

// Exportar también como default para next-intl
export default pt;
